import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { CartAddEvent, CartErrorEvent, ThemeEvents } from '@theme/events';
import { buildVariantPairSet, fieldMatches } from '@theme/pdp-cp-conditions';

const DEFAULT_SEP = '|||';

/**
 * @typedef {{ value: string; label: string; category?: string; sortOrder?: number; thumbUrl?: string; thumbUrlLarge?: string }} PdpCpOptionJson
 */

/**
 * Custom grouped select: single-choice shows placeholder then plain selected label (native-like);
 * multi-choice uses pills + Add. Optional per-option quantities (metaobject) encode as "N x label" in the property.
 *
 * @extends {Component<Record<string, never>>}
 */
class PdpCpCustomSelect extends Component {
  /** @type {HTMLInputElement | null} */
  #hidden = null;
  /** @type {HTMLButtonElement | null} */
  #face = null;
  /** @type {HTMLSpanElement | null} */
  #placeholderEl = null;
  /** @type {HTMLSpanElement | null} */
  #selectedLabel = null;
  /** @type {HTMLElement | null} */
  #pills = null;
  /** @type {HTMLElement | null} */
  #body = null;
  /** @type {HTMLButtonElement | null} */
  #trigger = null;
  /** @type {HTMLElement | null} */
  #panel = null;
  /** @type {HTMLElement | null} */
  #panelScroll = null;
  /** @type {PdpCpOptionJson[]} */
  #options = [];
  /** @type {Map<string, PdpCpOptionJson>} */
  #byValue = new Map();
  /** @type {string[]} */
  #order = [];
  /** @type {Map<string, number>} */
  #qty = new Map();
  #single = true;
  #required = false;
  #separator = DEFAULT_SEP;
  #qtyEnabled = false;
  /** @type {number} */
  #qtyMax = 99;
  /** Sum cap across all selected options; 0 = no limit. */
  /** @type {number} */
  #qtyTotalMax = 0;
  /** Min sum when quantity mode is on; 0 = no floor (beyond empty selection). */
  /** @type {number} */
  #qtyTotalMin = 0;
  #i18n = {
    add: 'Add color',
    addColors: 'Add colors',
    change: 'Change colors',
    remove: 'Remove',
    required: '',
    qtyIncrease: 'Increase quantity',
    qtyDecrease: 'Decrease quantity',
    thumbPreview: 'Preview larger image',
    colorChartOpen: 'View color chart',
    colorChartTitle: 'Color options',
    colorChartMaxInTotal: 'Maximum of {{ count }} colors in total',
    colorChartPerChoiceMax: 'Maximum of {{ count }} colors per choice',
    colorChartLimitProgress: '{{ current }} / {{ max }}',
    colorChartLimitProgressA11y: '{{ current }} of {{ max }} used',
    colorChartPartySelectOne: 'Select 1 more color',
    colorChartPartySelectOther: 'Select {{ count }} more colors',
    colorChartPartyComplete: "You're all set—let's party!",
    colorChartPartyA11yIncomplete: '{{ current }} of {{ max }} colors selected, {{ remaining }} more to go.',
    colorChartPartyA11yComplete: 'All {{ max }} colors selected.',
    colorChartPartyCta: 'Looks good!',
    colorChartPartyCtaPending: 'Complete your color picks to continue',
    colorChartClose: 'Close',
    qtyTotalMin: 'Add at least {{ count }} in total (across all options).',
    categoryToggle: 'Toggle {{ name }} category',
  };
  /** After last progress UI update (0–1); used to detect crossing 100% for confetti. */
  #colorChartLastProgressRatio = 0;
  /** @type {{ wrap: HTMLDivElement; img: HTMLImageElement } | null} */
  #thumbPreviewPopover = null;
  /** @type {ReturnType<typeof setTimeout> | null} */
  #thumbPreviewHideTimer = null;
  /** @type {HTMLElement | null} */
  #thumbPreviewAnchor = null;
  /** @type {string} */
  #placeholderText = '';
  /** @type {HTMLFormElement | null} */
  #form = null;
  /** @type {HTMLElement | null} */
  #selectBar = null;
  /** @type {boolean} */
  #colorChartEnabled = false;
  /** @type {string} */
  #colorChartTitleText = '';
  /** @type {HTMLButtonElement | null} */
  #colorChartOpenBtn = null;
  /** @type {HTMLDialogElement | null} */
  #colorChartDialog = null;
  /** @type {HTMLElement | null} */
  #colorChartGrid = null;
  /** @type {number} */
  #colorChartSavedScrollY = 0;
  /** @type {boolean} */
  #colorChartDocumentScrollLockActive = false;
  /**
   * From `pdp_cp_field_select` — ordered category labels matching option `category` (case/spacing
   * insensitive). When empty, option categories are sorted A–Z.
   * @type {string[]}
   */
  #categoryOrder = [];
  /** @type {AbortController | null} */
  #optionsFetchAbort = null;
  /** @type {boolean} */
  #collapsibleCategories = false;

  /** @returns {void} */
  #updatePanelScrollTopFade = () => {
    if (!this.#panel || !this.#panelScroll) return;
    const atTop = this.#panelScroll.scrollTop <= 0.5;
    this.#panel.classList.toggle('pdp-cp-select__panel--scrolled', !atTop);
  };

  #onPanelScroll = () => {
    this.#updatePanelScrollTopFade();
  };

  /**
   * Sort category headings: optional explicit order (from `select_category_order`), else A–Z;
   * options with no `category` sort before named groups.
   * @param {string | undefined} a
   * @param {string | undefined} b
   * @returns {number}
   */
  #compareCategoryHeadings(a, b) {
    const as = (a && String(a).trim()) || '';
    const bs = (b && String(b).trim()) || '';
    if (as === '' && bs === '') return 0;
    if (as === '') return -1;
    if (bs === '') return 1;
    const order = this.#categoryOrder;
    if (order && order.length > 0) {
      const ra = this.#categoryOrderRankForSort(as, order);
      const rb = this.#categoryOrderRankForSort(bs, order);
      if (ra !== rb) return ra - rb;
    }
    return as.toLowerCase().localeCompare(bs.toLowerCase());
  }

  /**
   * @param {string} nameNonEmpty
   * @param {string[]} order
   * @returns {number}
   */
  #categoryOrderRankForSort(nameNonEmpty, order) {
    const n = nameNonEmpty.toLowerCase();
    for (let i = 0; i < order.length; i++) {
      const t = String(order[i] || '')
        .trim()
        .toLowerCase();
      if (t && t === n) return i;
    }
    return 1000;
  }

  connectedCallback() {
    super.connectedCallback();
    this.#hidden = this.querySelector('[data-pdp-cp-value-input]');
    this.#face = this.querySelector('[data-pdp-cp-face]');
    this.#placeholderEl = this.querySelector('[data-pdp-cp-placeholder]');
    this.#selectedLabel = this.querySelector('[data-pdp-cp-selected-label]');
    this.#pills = this.querySelector('[data-pdp-cp-pills]');
    this.#body = this.querySelector('[data-pdp-cp-body]');
    this.#trigger = this.querySelector('[data-pdp-cp-trigger]');
    this.#panel = this.querySelector('[data-pdp-cp-panel]');
    this.#panelScroll = this.querySelector('[data-pdp-cp-panel-scroll]');
    if (!this.#hidden || !this.#face || !this.#placeholderEl || !this.#pills || !this.#trigger || !this.#panel || !this.#panelScroll) {
      return;
    }

    this.#selectBar = this.#face.closest('.pdp-cp-select__row-bar');

    const i18nScript = this.querySelector('script[type="application/json"][data-pdp-cp-i18n]');
    if (i18nScript?.textContent) {
      try {
        const j = JSON.parse(i18nScript.textContent.trim());
        if (typeof j.add === 'string') this.#i18n.add = j.add;
        if (typeof j.addColors === 'string') this.#i18n.addColors = j.addColors;
        if (typeof j.change === 'string') this.#i18n.change = j.change;
        if (typeof j.remove === 'string') this.#i18n.remove = j.remove;
        if (typeof j.required === 'string') this.#i18n.required = j.required;
        if (typeof j.qtyIncrease === 'string') this.#i18n.qtyIncrease = j.qtyIncrease;
        if (typeof j.qtyDecrease === 'string') this.#i18n.qtyDecrease = j.qtyDecrease;
        if (typeof j.thumbPreview === 'string') this.#i18n.thumbPreview = j.thumbPreview;
        if (typeof j.colorChartOpen === 'string') this.#i18n.colorChartOpen = j.colorChartOpen;
        if (typeof j.colorChartTitle === 'string') this.#i18n.colorChartTitle = j.colorChartTitle;
        if (typeof j.colorChartMaxInTotal === 'string') this.#i18n.colorChartMaxInTotal = j.colorChartMaxInTotal;
        if (typeof j.colorChartPerChoiceMax === 'string') this.#i18n.colorChartPerChoiceMax = j.colorChartPerChoiceMax;
        if (typeof j.colorChartLimitProgress === 'string') this.#i18n.colorChartLimitProgress = j.colorChartLimitProgress;
        if (typeof j.colorChartLimitProgressA11y === 'string') this.#i18n.colorChartLimitProgressA11y = j.colorChartLimitProgressA11y;
        if (typeof j.colorChartPartySelectOne === 'string') this.#i18n.colorChartPartySelectOne = j.colorChartPartySelectOne;
        if (typeof j.colorChartPartySelectOther === 'string') this.#i18n.colorChartPartySelectOther = j.colorChartPartySelectOther;
        if (typeof j.colorChartPartyComplete === 'string') this.#i18n.colorChartPartyComplete = j.colorChartPartyComplete;
        if (typeof j.colorChartPartyA11yIncomplete === 'string') this.#i18n.colorChartPartyA11yIncomplete = j.colorChartPartyA11yIncomplete;
        if (typeof j.colorChartPartyA11yComplete === 'string') this.#i18n.colorChartPartyA11yComplete = j.colorChartPartyA11yComplete;
        if (typeof j.colorChartPartyCta === 'string') this.#i18n.colorChartPartyCta = j.colorChartPartyCta;
        if (typeof j.colorChartPartyCtaPending === 'string') this.#i18n.colorChartPartyCtaPending = j.colorChartPartyCtaPending;
        if (typeof j.colorChartClose === 'string') this.#i18n.colorChartClose = j.colorChartClose;
        if (typeof j.qtyTotalMin === 'string') this.#i18n.qtyTotalMin = j.qtyTotalMin;
        if (typeof j.categoryToggle === 'string') this.#i18n.categoryToggle = j.categoryToggle;
      } catch {
        // keep defaults
      }
    }

    this.#colorChartEnabled = this.dataset.pdpCpColorChart === 'true';
    this.#colorChartTitleText = (this.dataset.pdpCpColorChartTitle || '').trim();
    this.#colorChartOpenBtn = this.querySelector('[data-pdp-cp-color-chart-open]');
    this.#colorChartOpenBtn?.addEventListener('click', this.#onColorChartOpenClick);

    this.#placeholderText = (this.dataset.placeholder || '').trim();
    if (this.#placeholderEl) {
      this.#placeholderEl.textContent = this.#placeholderText;
    }

    this.#single = this.dataset.singleChoice !== 'false';
    this.#required = this.dataset.required === 'true';
    if (this.dataset.separator) this.#separator = this.dataset.separator;
    this.#qtyEnabled = this.dataset.selectQuantityEnabled === 'true';
    const rawMax = Number.parseInt(String(this.dataset.selectQuantityMax || '99'), 10);
    this.#qtyMax = Number.isFinite(rawMax) ? Math.min(999, Math.max(2, rawMax)) : 99;
    const rawTotAttr = this.getAttribute('data-select-quantity-total-max');
    const rawTotStr =
      rawTotAttr != null && rawTotAttr !== ''
        ? rawTotAttr.trim()
        : String(this.dataset.selectQuantityTotalMax || '0');
    const rawTot = Number.parseInt(rawTotStr, 10);
    this.#qtyTotalMax =
      this.#qtyEnabled && Number.isFinite(rawTot) && rawTot >= 1 ? Math.min(9999, rawTot) : 0;
    const rawMinAttr = this.getAttribute('data-select-quantity-total-min');
    const rawMinStr =
      rawMinAttr != null && rawMinAttr !== ''
        ? rawMinAttr.trim()
        : String(this.dataset.selectQuantityTotalMin || '0');
    const rawMin = Number.parseInt(rawMinStr, 10);
    this.#qtyTotalMin =
      this.#qtyEnabled && Number.isFinite(rawMin) && rawMin >= 1 ? Math.min(9999, Math.trunc(rawMin)) : 0;

    this.#readQtyRulesFromJson();
    this.#readQtyLimitsFromHidden();
    this.#clampQtyTotalMinToMax();

    this.#collapsibleCategories = this.dataset.pdpCpCollapsibleCategories === 'true';

    const orderScript = this.querySelector('script[type="application/json"][data-pdp-cp-category-order]');
    if (orderScript?.textContent) {
      try {
        const j = JSON.parse(orderScript.textContent.trim());
        if (Array.isArray(j)) {
          this.#categoryOrder = j
            .map((x) => (typeof x === 'string' ? x.trim() : String(x).trim()))
            .filter((s) => s.length > 0);
        }
      } catch {
        this.#categoryOrder = [];
      }
    }

    const script = this.querySelector('script[type="application/json"][data-pdp-cp-select-options]');
    if (script?.textContent) {
      try {
        const raw = JSON.parse(script.textContent.trim());
        this.#options = Array.isArray(raw) ? raw : [];
      } catch {
        this.#options = [];
      }
    }

    for (const o of this.#options) {
      if (o?.value != null) this.#byValue.set(String(o.value), o);
    }

    this.#buildPanelRows();

    const initial = this.#hidden.value?.trim();
    this.#order = [];
    this.#qty.clear();
    if (initial) {
      if (this.#qtyEnabled) {
        this.#parseQtyHidden(initial);
      } else if (this.#single) {
        this.#order = [initial];
      } else {
        this.#order = initial.split(this.#separator).filter(Boolean);
      }
    }

    this.#syncHiddenFromSelected();

    this.#renderPills();
    this.#updateChrome();
    this.#updateRowPressedStates();
    this.#refreshQtyDependentUi();
    this.#updateValidity();

    this.#face.addEventListener('click', this.#handleFaceClick);
    this.#trigger?.addEventListener('click', this.#handleTriggerClick);
    this.#selectBar?.addEventListener('click', this.#handleSelectBarClick);
    this.#panelScroll.addEventListener('click', this.#handlePanelClick);
    this.#panelScroll.addEventListener('scroll', this.#onPanelScroll, { passive: true });
    this.#pills.addEventListener('click', this.#handlePillsStripClick);
    document.addEventListener('click', this.#onDocClick, true);
    document.addEventListener('keydown', this.#onKeydown);

    const formId = this.dataset.formId;
    if (formId) {
      const form = document.getElementById(formId);
      if (form instanceof HTMLFormElement) {
        this.#form = form;
        form.addEventListener('submit', this.#onFormSubmit);
      }
    }

    this.#maybeFetchAllOptions();
  }

  /**
   * Fetch the full option list from the Storefront API when the metaobject id, storefront token,
   * and shop domain attributes are present. Bypasses the Liquid 50-iteration `for` cap that
   * truncates the inlined `<script data-pdp-cp-select-options>` JSON for fields with >50 options.
   * @returns {void}
   */
  #maybeFetchAllOptions = () => {
    let metaobjectId = (this.dataset.pdpCpMetaobjectId || '').trim();
    const token = (this.dataset.pdpCpStorefrontToken || '').trim();
    const shopDomain = (this.dataset.pdpCpShopDomain || '').trim();
    if (!metaobjectId || !token || !shopDomain) return;
    if (!metaobjectId.startsWith('gid://')) {
      metaobjectId = `gid://shopify/Metaobject/${metaobjectId}`;
    }
    this.#optionsFetchAbort?.abort();
    this.#optionsFetchAbort = new AbortController();
    this.#setOptionsLoading(true);
    this.#fetchAllOptions(metaobjectId, token, shopDomain, this.#optionsFetchAbort.signal)
      .then((fetched) => {
        if (Array.isArray(fetched) && fetched.length > 0) {
          this.#applyFetchedOptions(fetched);
        }
      })
      .catch((err) => {
        if (err?.name === 'AbortError') return;
        console.warn('pdp-cp options fetch failed', err);
      })
      .finally(() => {
        this.#setOptionsLoading(false);
      });
  };

  /**
   * @param {string} metaobjectId
   * @param {string} token
   * @param {string} shopDomain
   * @param {AbortSignal} signal
   * @returns {Promise<PdpCpOptionJson[]>}
   */
  async #fetchAllOptions(metaobjectId, token, shopDomain, signal) {
    const query = `
      query OptionsForField($id: ID!) {
        metaobject(id: $id) {
          options: field(key: "options") {
            references(first: 250) {
              nodes {
                ... on Metaobject {
                  id
                  label: field(key: "label") { value }
                  optionValue: field(key: "value") { value }
                  category: field(key: "category") { value }
                  sortOrder: field(key: "sort_order") { value }
                  thumbnail: field(key: "thumbnail") {
                    reference {
                      ... on MediaImage {
                        image {
                          thumbSmall: url(transform: { maxWidth: 128, maxHeight: 128 })
                          thumbLarge: url(transform: { maxWidth: 800, maxHeight: 800 })
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    `;
    const res = await fetch(`https://${shopDomain}/api/2026-01/graphql.json`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': token,
      },
      body: JSON.stringify({ query, variables: { id: metaobjectId } }),
      signal,
    });
    if (!res.ok) throw new Error(`Storefront API error: ${res.status}`);
    const data = await res.json();
    const nodes = data?.data?.metaobject?.options?.references?.nodes;
    if (!Array.isArray(nodes)) return [];
    return nodes.map((n, i) => {
      const thumbSmall = n?.thumbnail?.reference?.image?.thumbSmall || '';
      const thumbLarge = n?.thumbnail?.reference?.image?.thumbLarge || '';
      const sortRaw = n?.sortOrder?.value;
      const sortNum = sortRaw != null && sortRaw !== '' ? Number(sortRaw) : i;
      return {
        value: n?.optionValue?.value ?? '',
        label: n?.label?.value ?? '',
        category: (n?.category?.value ?? '').trim(),
        sortOrder: Number.isFinite(sortNum) ? sortNum : i,
        thumbUrl: thumbSmall,
        thumbUrlLarge: thumbLarge,
      };
    });
  }

  /**
   * Replace `#options` with the fetched list, rebuild `#byValue`, re-render the panel, and
   * re-run the hidden-input restore so any saved/cart selections referring to options beyond
   * the first 50 (which were missing from the initial Liquid JSON) become resolvable.
   * @param {PdpCpOptionJson[]} fetched
   * @returns {void}
   */
  #applyFetchedOptions(fetched) {
    this.#options = fetched;
    this.#byValue.clear();
    for (const o of fetched) {
      if (o?.value != null) this.#byValue.set(String(o.value), o);
    }
    this.#buildPanelRows();

    const initial = this.#hidden?.value?.trim() || '';
    this.#order = [];
    this.#qty.clear();
    if (initial) {
      if (this.#qtyEnabled) {
        this.#parseQtyHidden(initial);
      } else if (this.#single) {
        this.#order = [initial];
      } else {
        this.#order = initial.split(this.#separator).filter(Boolean);
      }
    }
    this.#syncHiddenFromSelected();
    this.#renderPills();
    this.#updateChrome();
    this.#updateRowPressedStates();
    this.#refreshQtyDependentUi();
    this.#updateValidity();

    if (this.#colorChartDialog?.open) {
      this.#buildOrRefreshColorChartGrid();
    }
  }

  /**
   * Toggle a loading marker class on the host element so CSS can show a subtle hint while
   * the Storefront fetch is in flight.
   * @param {boolean} on
   * @returns {void}
   */
  #setOptionsLoading(on) {
    this.classList.toggle('pdp-cp-select--options-loading', on);
    if (on) {
      this.setAttribute('data-pdp-cp-options-loading', '');
      this.setAttribute('aria-busy', 'true');
    } else {
      this.removeAttribute('data-pdp-cp-options-loading');
      this.removeAttribute('aria-busy');
    }
  }

  disconnectedCallback() {
    this.#optionsFetchAbort?.abort();
    this.#optionsFetchAbort = null;
    this.#destroyColorChartDialog();
    this.#colorChartOpenBtn?.removeEventListener('click', this.#onColorChartOpenClick);
    this.#destroyThumbPreviewPopover();
    document.removeEventListener('click', this.#onDocClick, true);
    document.removeEventListener('keydown', this.#onKeydown);
    this.#form?.removeEventListener('submit', this.#onFormSubmit);
    this.#face?.removeEventListener('click', this.#handleFaceClick);
    this.#trigger?.removeEventListener('click', this.#handleTriggerClick);
    this.#selectBar?.removeEventListener('click', this.#handleSelectBarClick);
    this.#panelScroll?.removeEventListener('click', this.#handlePanelClick);
    this.#panelScroll?.removeEventListener('scroll', this.#onPanelScroll);
    this.#pills?.removeEventListener('click', this.#handlePillsStripClick);
    super.disconnectedCallback();
  }

  /**
   * Values joined by separator (for preset reconciliation).
   * @returns {string}
   */
  getValueForPresetCompare() {
    return this.#order.join(this.#separator);
  }

  /**
   * Same separator as {@link PdpCpCustomSelect#getValueForPresetCompare} (for parent preset checks).
   * @returns {string}
   */
  getJoinSeparator() {
    return this.#separator;
  }

  /**
   * @param {string} value - option `value` key
   * @returns {number} units for that key when in quantity mode; 1 if not qty mode and selected; 0 if not selected
   */
  getQuantityForOptionValue(value) {
    if (!this.#order.includes(value)) return 0;
    if (!this.#qtyEnabled) return 1;
    return this.#getQty(value);
  }

  /**
   * @param {string} value
   * @param {number} [perOptionQuantity] - when per-option quantity is enabled, how many of `value` (clamped to limits).
   */
  setValueFromPreset(value, perOptionQuantity) {
    if (!this.#hidden) return;
    this.#order = [];
    this.#qty.clear();
    if (value) {
      this.#order = [value];
      if (this.#qtyEnabled) {
        let n = 1;
        if (perOptionQuantity != null && Number.isFinite(perOptionQuantity) && perOptionQuantity > 0) {
          n = Math.min(this.#qtyMax, Math.max(1, Math.floor(perOptionQuantity)));
        }
        this.#qty.set(value, n);
      }
    }
    this.#syncHiddenFromSelected();
    this.#renderPills();
    this.#updateChrome();
    this.#updateRowPressedStates();
    this.#refreshQtyDependentUi();
    this.#updateValidity();
  }

  /**
   * Multi-select preset: one field, many option values in one update (avoids each mapping overwriting the last).
   * @param {{ value: string; quantity?: unknown }[]} entries
   */
  setSelectionFromPreset(entries) {
    if (!this.#hidden) return;
    if (!Array.isArray(entries) || entries.length === 0) {
      this.setValueFromPreset('');
      return;
    }
    if (this.#single) {
      const e0 = /** @type {{ value: unknown; quantity?: unknown }} */ (entries[0]);
      const q0 = e0?.quantity;
      this.setValueFromPreset(
        String(e0.value ?? ''),
        q0 != null && Number.isFinite(Number(q0)) && Number(q0) > 0 ? Math.floor(Number(q0)) : undefined
      );
      return;
    }
    this.#order = [];
    this.#qty.clear();
    for (const e of entries) {
      if (!e || e.value == null) continue;
      const v = String(e.value).trim();
      if (!v) continue;
      if (!this.#order.includes(v)) this.#order.push(v);
      if (this.#qtyEnabled) {
        let add = 1;
        const q = e.quantity;
        if (q != null && Number.isFinite(Number(q)) && Number(q) > 0) {
          add = Math.min(this.#qtyMax, Math.max(1, Math.floor(Number(q))));
        }
        this.#qty.set(v, (this.#qty.get(v) || 0) + add);
      }
    }
    this.#syncHiddenFromSelected();
    this.#renderPills();
    this.#updateChrome();
    this.#updateRowPressedStates();
    this.#refreshQtyDependentUi();
    this.#updateValidity();
  }

  /**
   * @param {string} v
   * @param {{ closePanel: boolean; closeDialog: boolean }} opts
   */
  #applySingleChoiceValue(v, opts) {
    this.#order = [v];
    if (this.#qtyEnabled) {
      this.#qty.clear();
      this.#qty.set(v, 1);
    }
    this.#syncHiddenFromSelected();
    this.#renderPills();
    this.#updateChrome();
    this.#updateRowPressedStates();
    this.#refreshQtyDependentUi();
    this.#updateValidity();
    if (opts.closePanel) this.#closePanel();
    if (opts.closeDialog && this.#colorChartDialog?.open) {
      this.#colorChartDialog.close();
    } else {
      this.#syncColorChartGridSelection();
    }
  }

  /**
   * @param {string} tpl
   * @param {number} n
   * @returns {string}
   */
  #applyI18nCountTemplate(tpl, n) {
    if (typeof tpl !== 'string') return '';
    const s = String(n);
    return tpl
      .replace(/\{count\}/g, s)
      .replace(/\{\{\s*count\s*\}\}/g, s);
  }

  /**
   * "Maximum of 1 colors" → "Maximum of 1 color" for en-style copy; no-op for other phrasings.
   * @param {string} tpl
   * @param {number} n
   * @returns {string}
   */
  #enColorLimitPhrase(tpl, n) {
    const raw = this.#applyI18nCountTemplate(tpl, n);
    if (n === 1) {
      return raw.replace(/\b1 colors\b/gi, '1 color');
    }
    return raw;
  }

  /**
   * Dialog header: field title only (party limit row is a separate footer; shown only with a min).
   * @returns {string}
   */
  #getColorChartTitleBase() {
    return (this.#colorChartTitleText || this.#i18n.colorChartTitle || 'Color options').trim();
  }

  /**
   * Max units for the color chart bar (0 = hide bar: no qty or no selection ceiling).
   * @returns {number}
   */
  #getColorChartProgressCap() {
    if (!this.#qtyEnabled) return 0;
    if (this.#qtyTotalMax > 0) return this.#qtyTotalMax;
    if (this.#qtyTotalMin > 0) return this.#qtyTotalMin;
    if (this.#order.length < 1) return 0;
    return this.#order.length * this.#qtyMax;
  }

  /**
   * @returns {number} 0–1
   */
  #getColorChartProgressRatio() {
    const cap = this.#getColorChartProgressCap();
    if (cap <= 0) return 0;
    return Math.min(1, this.#totalQty() / cap);
  }

  /**
   * @param {string} tpl
   * @param {number} cur
   * @param {number} cap
   * @returns {string}
   */
  #formatColorChartProgressText(tpl, cur, cap) {
    if (typeof tpl !== 'string') return `${cur} / ${cap}`;
    return tpl
      .replace(/\{current\}/g, String(cur))
      .replace(/\{\{\s*current\s*\}\}/g, String(cur))
      .replace(/\{max\}/g, String(cap))
      .replace(/\{\{\s*max\s*\}\}/g, String(cap));
  }

  /**
   * Party footer (heading, progress, CTA): only when a total minimum is configured and the field
   * uses quantity + required + a computable cap; hidden entirely when that minimum rule is off.
   * @returns {boolean}
   */
  #showColorChartPartyFooter() {
    return (
      this.#qtyEnabled &&
      this.#required &&
      this.#qtyTotalMin > 0 &&
      this.#getColorChartProgressCap() > 0
    );
  }

  /**
   * @param {string} tpl
   * @param {Record<string, string | number>} vars
   * @returns {string}
   */
  #formatColorChartPartyText(tpl, vars) {
    if (typeof tpl !== 'string') return '';
    let s = tpl;
    for (const [k, v] of Object.entries(vars)) {
      s = s.replace(new RegExp(`\\{\\{\\s*${k}\\s*\\}\\}`, 'g'), String(v));
    }
    return s;
  }

  #updateColorChartDialogUi = () => {
    if (!this.#colorChartDialog) return;
    const prevProgressRatio = this.#colorChartLastProgressRatio;
    const base = this.#getColorChartTitleBase();
    const h2 = this.#colorChartDialog.querySelector('.pdp-cp-color-chart-dialog__title');
    if (h2 instanceof HTMLElement) h2.textContent = base;

    const cap = this.#getColorChartProgressCap();
    const tq = this.#totalQty();
    const ratio = cap > 0 ? Math.min(1, tq / cap) : 0;
    const footer = this.#colorChartDialog.querySelector('[data-pdp-cp-cc-limit-footer]');
    const desc = this.#colorChartDialog.querySelector('[data-pdp-cp-cc-limit-desc]');
    const labelEl = this.#colorChartDialog.querySelector('[data-pdp-cp-cc-limit-label]');
    const fill = this.#colorChartDialog.querySelector('[data-pdp-cp-cc-limit-fill]');
    const track = this.#colorChartDialog.querySelector('[data-pdp-cp-cc-limit-track]');
    const cta = this.#colorChartDialog.querySelector('[data-pdp-cp-cc-party-cta]');

    const show = this.#showColorChartPartyFooter();
    if (footer instanceof HTMLElement) {
      footer.toggleAttribute('hidden', !show);
    }

    if (show) {
      const rem = Math.max(0, cap - tq);
      const vars = { current: tq, max: cap, remaining: rem, count: rem };
      let vis;
      if (ratio >= 1) {
        vis = this.#formatColorChartPartyText(this.#i18n.colorChartPartyComplete, vars);
      } else if (rem === 1) {
        vis = this.#i18n.colorChartPartySelectOne;
      } else {
        vis = this.#formatColorChartPartyText(this.#i18n.colorChartPartySelectOther, { count: rem });
      }
      const a11y =
        ratio >= 1
          ? this.#formatColorChartPartyText(this.#i18n.colorChartPartyA11yComplete, vars)
          : this.#formatColorChartPartyText(this.#i18n.colorChartPartyA11yIncomplete, vars);
      if (labelEl instanceof HTMLElement) labelEl.textContent = vis;
      if (desc instanceof HTMLElement) {
        desc.textContent = a11y;
      }
      if (fill instanceof HTMLElement) {
        fill.style.setProperty('--pdp-cp-cc-fill', String(Math.max(0, Math.min(100, ratio * 100))));
      }
      if (track instanceof HTMLElement) {
        track.removeAttribute('hidden');
        track.setAttribute('role', 'progressbar');
        track.setAttribute('aria-valuemin', '0');
        track.setAttribute('aria-valuemax', String(cap));
        track.setAttribute('aria-valuenow', String(tq));
        track.setAttribute('aria-valuetext', a11y);
      }
      if (cta instanceof HTMLButtonElement) {
        cta.textContent = this.#i18n.colorChartPartyCta;
        const full = ratio >= 1;
        cta.disabled = !full;
        cta.title = full ? '' : this.#i18n.colorChartPartyCtaPending;
      }
      const motionOk = !window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
      if (motionOk && prevProgressRatio < 1 && ratio >= 1 && cap > 0) {
        this.#fireColorChartConfetti();
      }
      this.#colorChartLastProgressRatio = ratio;
    } else {
      this.#colorChartLastProgressRatio = 0;
    }

    const descId = desc?.id;
    if (this.#colorChartGrid instanceof HTMLElement) {
      this.#colorChartGrid.setAttribute('aria-label', base);
      if (show && descId) {
        this.#colorChartGrid.setAttribute('aria-describedby', descId);
      } else {
        this.#colorChartGrid.removeAttribute('aria-describedby');
      }
    }
  };

  /**
   * Short burst; skipped when `prefers-reduced-motion: reduce` (handled in caller).
   */
  #fireColorChartConfetti() {
    const host = document.createElement('div');
    host.setAttribute('aria-hidden', 'true');
    host.className = 'pdp-cp-color-chart-confetti';
    host.style.cssText =
      'position:fixed;inset:0;pointer-events:none;z-index:2147483000;overflow:hidden';
    const colors = [
      '#c026d3',
      '#d946ef',
      '#7c3aed',
      '#f472b6',
      '#facc15',
      '#fb923c',
      '#4ade80',
      '#22d3ee',
      '#fff',
    ];
    const w = window.innerWidth;
    const h = window.innerHeight;
    /**
     * @param {number} count
     * @param {number} yFrac
     * @param {number} spread
     */
    const emit = (count, yFrac, spread) => {
      for (let i = 0; i < count; i++) {
        const p = document.createElement('span');
        const x = w * (0.1 + Math.random() * 0.8);
        const y0 = h * yFrac;
        const wpx = 4 + Math.random() * 7;
        const hpx = 5 + Math.random() * 8;
        const round = Math.random() > 0.4;
        const br = round ? '999px' : '2px';
        p.style.cssText = `position:absolute;left:${x}px;top:${y0}px;width:${wpx}px;height:${hpx}px;border-radius:${br};background:${
          colors[Math.floor(Math.random() * colors.length)]
        };box-shadow:0 0 6px ${colors[Math.floor(Math.random() * colors.length)]}66;opacity:0.95;transform:translate(-50%,0);will-change:transform,opacity`;
        const ang = (Math.random() - 0.5) * (spread * 1.1);
        const dist = 220 + Math.random() * 380;
        const tx = Math.sin(ang) * dist;
        const ty = -0.55 * dist * (0.5 + Math.random() * 0.55) - 40;
        const rot = 420 * (0.2 + Math.random() * 0.8) * (Math.random() > 0.5 ? 1 : -1);
        p.animate(
          [
            { transform: 'translate(-50%,0) rotate(0deg) scale(1)', opacity: 0.95 },
            {
              transform: `translate(calc(-50% + ${tx}px),${ty}px) rotate(${rot}deg) scale(0.4)`,
              opacity: 0.08,
            },
          ],
          { duration: 1600 + Math.random() * 800, easing: 'cubic-bezier(0.12,0.45,0.2,0.99)', fill: 'forwards' },
        );
        host.appendChild(p);
      }
    };
    emit(100, 0.84, 1.3);
    setTimeout(() => {
      emit(56, 0.78, 1.5);
    }, 60);
    document.body.appendChild(host);
    setTimeout(() => {
      host.remove();
    }, 3000);
  }

  /** Match `dialog.js` + theme `html[scroll-lock]` so the page does not scroll behind the modal. */
  #lockDocumentScrollForColorChart() {
    if (this.#colorChartDocumentScrollLockActive) return;
    this.#colorChartSavedScrollY = window.scrollY;
    this.#colorChartDocumentScrollLockActive = true;
    document.documentElement.setAttribute('scroll-lock', '');
    document.body.style.setProperty('width', '100%');
    document.body.style.setProperty('position', 'fixed');
    document.body.style.setProperty('top', `-${this.#colorChartSavedScrollY}px`);
  }

  #unlockDocumentScrollForColorChart() {
    if (!this.#colorChartDocumentScrollLockActive) return;
    this.#colorChartDocumentScrollLockActive = false;
    document.documentElement.removeAttribute('scroll-lock');
    document.body.style.removeProperty('width');
    document.body.style.removeProperty('position');
    document.body.style.removeProperty('top');
    window.scrollTo({ top: this.#colorChartSavedScrollY, behavior: 'instant' });
  }

  #destroyColorChartDialog() {
    this.#colorChartGrid?.removeEventListener('click', this.#onColorChartGridClick);
    if (this.#colorChartDialog) {
      if (this.#colorChartDialog.open) {
        this.#colorChartDialog.close();
      }
      this.#colorChartDialog.remove();
    }
    this.#colorChartDialog = null;
    this.#colorChartGrid = null;
    this.#colorChartLastProgressRatio = 0;
  }

  #ensureColorChartDialog() {
    if (this.#colorChartDialog && this.#colorChartGrid) return;
    const d = document.createElement('dialog');
    const modalScheme = (this.dataset.pdpCpColorChartModalColorScheme || 'scheme-9').trim() || 'scheme-9';
    d.className = `pdp-cp-color-chart-dialog color-${modalScheme}`;
    d.setAttribute('aria-modal', 'true');

    const idSlug = (this.dataset.internalKey || 'pdp-cp')
      .toString()
      .replace(/[^a-zA-Z0-9_-]/g, '-');
    const titleId = `pdp-cp-cc-title--${idSlug}`;
    d.setAttribute('aria-labelledby', titleId);

    const header = document.createElement('div');
    header.className = 'pdp-cp-color-chart-dialog__header';
    const h2 = document.createElement('h2');
    h2.id = titleId;
    h2.className = 'pdp-cp-color-chart-dialog__title';
    h2.textContent = this.#getColorChartTitleBase();

    const closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'pdp-cp-color-chart-dialog__close';
    closeBtn.setAttribute('data-pdp-cp-color-chart-close', '');
    closeBtn.setAttribute('aria-label', this.#i18n.colorChartClose);
    const closeIconEl = this.closest('pdp-custom-properties-component')?.querySelector(
      'template[data-pdp-cp-color-chart-close-icon]',
    );
    if (closeIconEl instanceof HTMLTemplateElement) {
      closeBtn.appendChild(closeIconEl.content.cloneNode(true));
      closeBtn.querySelector('svg')?.setAttribute('aria-hidden', 'true');
    } else {
      closeBtn.textContent = '\u00D7';
    }

    const body = document.createElement('div');
    body.className = 'pdp-cp-color-chart-dialog__body';
    const grid = document.createElement('div');
    grid.className = 'pdp-cp-color-chart__grid';
    grid.setAttribute('role', 'group');
    grid.setAttribute('aria-label', this.#getColorChartTitleBase());
    body.appendChild(grid);
    header.append(h2, closeBtn);

    const limitDescId = `pdp-cp-cc-limit-desc--${idSlug}`;
    const footer = document.createElement('div');
    footer.className = 'pdp-cp-color-chart-dialog__footer';
    footer.setAttribute('data-pdp-cp-cc-limit-footer', '');
    footer.hidden = true;

    const limitDesc = document.createElement('p');
    limitDesc.className = 'visually-hidden';
    limitDesc.setAttribute('data-pdp-cp-cc-limit-desc', '');
    limitDesc.id = limitDescId;

    const label = document.createElement('div');
    label.className = 'pdp-cp-color-chart-dialog__limit-heading';
    label.setAttribute('data-pdp-cp-cc-limit-label', '');

    const track = document.createElement('div');
    track.className = 'pdp-cp-color-chart-dialog__limit-track';
    track.setAttribute('data-pdp-cp-cc-limit-track', '');

    const fill = document.createElement('div');
    fill.className = 'pdp-cp-color-chart-dialog__limit-fill';
    fill.setAttribute('data-pdp-cp-cc-limit-fill', '');

    const partyCta = document.createElement('button');
    partyCta.type = 'button';
    partyCta.className = 'pdp-cp-color-chart-dialog__party-cta';
    partyCta.setAttribute('data-pdp-cp-cc-party-cta', '');
    partyCta.disabled = true;
    partyCta.textContent = this.#i18n.colorChartPartyCta;
    partyCta.title = this.#i18n.colorChartPartyCtaPending;

    track.appendChild(fill);
    footer.append(limitDesc, label, track, partyCta);
    partyCta.addEventListener('click', () => {
      if (!partyCta.disabled) d.close();
    });

    d.append(header, body, footer);
    closeBtn.addEventListener('click', () => d.close());
    d.addEventListener('click', (e) => {
      if (e.target === d) d.close();
    });
    d.addEventListener('close', () => {
      this.#unlockDocumentScrollForColorChart();
      this.#colorChartOpenBtn?.focus();
    });
    grid.addEventListener('click', this.#onColorChartGridClick);

    document.body.appendChild(d);
    this.#colorChartDialog = d;
    this.#colorChartGrid = grid;
  }

  #buildOrRefreshColorChartGrid() {
    const grid = this.#colorChartGrid;
    if (!grid) return;
    grid.replaceChildren();

    const sorted = [...this.#options].sort((a, b) => {
      const c = this.#compareCategoryHeadings(a?.category, b?.category);
      if (c !== 0) return c;
      return (a.sortOrder ?? 0) - (b.sortOrder ?? 0);
    });

    let lastCat = /** @type {string | null} */ (null);
    for (const opt of sorted) {
      const cat = (opt.category || '').trim();
      if (cat !== lastCat) {
        lastCat = cat;
        if (cat) {
          const head = document.createElement('p');
          head.className = 'pdp-cp-color-chart__category';
          head.setAttribute('role', 'presentation');
          head.textContent = cat;
          grid.appendChild(head);
        }
      }

      const valStr = String(opt.value);
      const item = document.createElement('div');
      item.className = 'pdp-cp-color-chart__item';
      item.setAttribute('data-pdp-cp-color-chart-item', '');
      item.setAttribute('data-pdp-cp-cc-v', valStr);

      const figure = document.createElement('div');
      figure.className = 'pdp-cp-color-chart__tile-figure';

      const labText = opt.label ?? valStr;
      const tile = document.createElement('button');
      tile.type = 'button';
      tile.className = 'pdp-cp-color-chart__tile';
      tile.setAttribute('data-pdp-cp-color-chart-tile', valStr);
      const inner = document.createElement('div');
      inner.className = 'pdp-cp-color-chart__tile-inner';

      const media = document.createElement('div');
      media.className = 'pdp-cp-color-chart__tile-media';
      const previewSrc = (opt.thumbUrlLarge && String(opt.thumbUrlLarge).trim()) || opt.thumbUrl;
      if (previewSrc) {
        const img = document.createElement('img');
        img.className = 'pdp-cp-color-chart__tile-img';
        img.src = previewSrc;
        img.alt = '';
        img.setAttribute('aria-hidden', 'true');
        img.width = 160;
        img.height = 160;
        img.loading = 'lazy';
        media.appendChild(img);
      } else {
        const ph = document.createElement('div');
        ph.className = 'pdp-cp-color-chart__tile-placeholder';
        ph.setAttribute('aria-hidden', 'true');
        media.appendChild(ph);
      }
      const cap = document.createElement('span');
      cap.className = 'pdp-cp-color-chart__tile-label';
      cap.textContent = labText;
      cap.setAttribute('aria-hidden', 'true');
      inner.appendChild(media);
      inner.appendChild(cap);
      tile.setAttribute('aria-label', labText);
      tile.appendChild(inner);
      figure.appendChild(tile);

      if (this.#qtyEnabled) {
        const qtyWrap = document.createElement('div');
        qtyWrap.className = 'pdp-cp-color-chart__tile-qty';
        qtyWrap.setAttribute('data-pdp-cp-color-chart-tile-qty', '');
        qtyWrap.hidden = true;
        const row = document.createElement('div');
        row.className = 'pdp-cp-color-chart__tile-qty-row';
        row.setAttribute('data-pdp-cp-row-qty', '');

        const dec = document.createElement('button');
        dec.type = 'button';
        dec.className = 'pdp-cp-color-chart__tile-qty-btn';
        dec.setAttribute('data-pdp-cp-qty-dec', '');

        const readout = document.createElement('span');
        readout.className = 'pdp-cp-color-chart__tile-qty-readout';
        readout.setAttribute('data-pdp-cp-qty-readout', '');
        readout.setAttribute('aria-live', 'polite');

        const inc = document.createElement('button');
        inc.type = 'button';
        inc.className = 'pdp-cp-color-chart__tile-qty-btn';
        inc.setAttribute('data-pdp-cp-qty-inc', '');

        dec.textContent = '−';
        inc.textContent = '+';

        row.append(dec, readout, inc);
        qtyWrap.appendChild(row);
        figure.appendChild(qtyWrap);
      }

      item.appendChild(figure);

      grid.appendChild(item);
    }
    this.#syncColorChartGridSelection();
  }

  #syncColorChartGridSelection() {
    if (!this.#colorChartGrid) return;
    for (const btn of this.#colorChartGrid.querySelectorAll('[data-pdp-cp-color-chart-tile]')) {
      if (!(btn instanceof HTMLButtonElement)) continue;
      const v = btn.getAttribute('data-pdp-cp-color-chart-tile') ?? '';
      if (this.#single) {
        const cur = this.#order[0];
        if (v === cur) {
          btn.setAttribute('aria-current', 'true');
        } else {
          btn.removeAttribute('aria-current');
        }
        btn.removeAttribute('aria-pressed');
      } else {
        const on = this.#order.includes(v);
        btn.setAttribute('aria-pressed', on ? 'true' : 'false');
        btn.removeAttribute('aria-current');
      }
    }
    this.#syncColorChartGridQtyUI();
    this.#updateColorChartDialogUi();
  }

  /** Show/update per-option quantity in the color chart (matches dropdown list). */
  #syncColorChartGridQtyUI() {
    if (!this.#colorChartGrid || !this.#qtyEnabled) return;
    for (const item of this.#colorChartGrid.querySelectorAll('[data-pdp-cp-color-chart-item]')) {
      if (!(item instanceof HTMLElement)) continue;
      const v = item.getAttribute('data-pdp-cp-cc-v') ?? '';
      const wrap = item.querySelector('[data-pdp-cp-color-chart-tile-qty]');
      const rowInner = item.querySelector('[data-pdp-cp-row-qty]');
      if (!(wrap instanceof HTMLElement) || !rowInner) continue;
      const selected = this.#single ? this.#order[0] === v : this.#order.includes(v);
      if (selected) {
        wrap.removeAttribute('hidden');
      } else {
        wrap.setAttribute('hidden', '');
      }
      if (!selected) continue;
      const q = this.#getQty(v);
      const lab = this.#labelFor(v);
      const readout = rowInner.querySelector('[data-pdp-cp-qty-readout]');
      const dec = rowInner.querySelector('[data-pdp-cp-qty-dec]');
      const inc = rowInner.querySelector('[data-pdp-cp-qty-inc]');
      if (readout) readout.textContent = String(q);
      if (dec instanceof HTMLButtonElement) {
        dec.disabled = false;
        dec.setAttribute('aria-label', `${this.#i18n.qtyDecrease}, ${lab}`);
      }
      if (inc instanceof HTMLButtonElement) {
        inc.disabled = !this.#canIncreaseQty(v);
        inc.setAttribute('aria-label', `${this.#i18n.qtyIncrease}, ${lab}`);
      }
    }
  }

  /** @param {MouseEvent} e */
  #onColorChartGridClick = (e) => {
    const t = e.target;
    if (!(t instanceof Element)) return;

    const inc = t.closest('[data-pdp-cp-qty-inc]');
    const dec = t.closest('[data-pdp-cp-qty-dec]');
    if (inc || dec) {
      e.preventDefault();
      e.stopPropagation();
      if (!this.#qtyEnabled) return;
      const item = t.closest('[data-pdp-cp-color-chart-item]');
      const v = item?.getAttribute('data-pdp-cp-cc-v');
      if (v == null) return;

      const q = this.#getQty(v);
      if (inc) {
        if (this.#canIncreaseQty(v)) this.#qty.set(v, q + 1);
      } else if (q <= 1) {
        this.#removeFromOrder(v);
      } else {
        this.#qty.set(v, q - 1);
      }

      this.#syncHiddenFromSelected();
      this.#renderPills();
      this.#updateChrome();
      this.#updateRowPressedStates();
      this.#refreshQtyDependentUi();
      this.#updateValidity();
      this.#syncColorChartGridSelection();
      return;
    }

    const tile = t.closest('[data-pdp-cp-color-chart-tile]');
    if (!tile || !(tile instanceof HTMLButtonElement)) return;
    if (!this.#colorChartGrid?.contains(tile)) return;
    const v = tile.getAttribute('data-pdp-cp-color-chart-tile');
    if (v == null) return;
    e.preventDefault();
    e.stopPropagation();
    if (this.#single) {
      this.#applySingleChoiceValue(v, { closePanel: false, closeDialog: true });
      return;
    }
    const i = this.#order.indexOf(v);
    if (i >= 0) {
      this.#removeFromOrder(v);
    } else {
      if (!this.#canAddNewOptionLine()) return;
      this.#order.push(v);
      if (this.#qtyEnabled) this.#qty.set(v, 1);
    }
    this.#syncHiddenFromSelected();
    this.#renderPills();
    this.#updateChrome();
    this.#updateRowPressedStates();
    this.#refreshQtyDependentUi();
    this.#updateValidity();
    this.#syncColorChartGridSelection();
  };

  #openColorChartModal = () => {
    if (!this.#colorChartEnabled) return;
    this.#ensureColorChartDialog();
    this.#buildOrRefreshColorChartGrid();
    this.#colorChartLastProgressRatio = this.#showColorChartPartyFooter() ? this.#getColorChartProgressRatio() : 0;
    this.#updateColorChartDialogUi();
    const d = this.#colorChartDialog;
    if (!d) return;
    requestAnimationFrame(() => {
      this.#lockDocumentScrollForColorChart();
      d.showModal();
      const c = d.querySelector('[data-pdp-cp-color-chart-close]');
      if (c instanceof HTMLElement) c.focus();
    });
  };

  /** @param {MouseEvent} e */
  #onColorChartOpenClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    this.#closePanel();
    this.#openColorChartModal();
  };

  /**
   * @param {string} label
   * @returns {string | null}
   */
  #valueFromLabel(label) {
    const t = label.trim();
    for (const o of this.#options) {
      if (String(o.label ?? '').trim() === t) return String(o.value);
    }
    return null;
  }

  /**
   * @param {string} s
   */
  #parseQtyHidden(s) {
    for (const part of s.split(this.#separator)) {
      const t = part.trim();
      if (!t) continue;
      const m = t.match(/^(\d+)\s*x\s*(.+)$/i);
      if (m?.[1] != null && m[2] != null) {
        let q = Number.parseInt(m[1], 10);
        const labelPart = String(m[2]).trim();
        const val = this.#valueFromLabel(labelPart) ?? (this.#byValue.has(labelPart) ? labelPart : null);
        if (val == null) continue;
        q = Math.min(this.#qtyMax, Math.max(1, Number.isFinite(q) ? q : 1));
        this.#order.push(val);
        this.#qty.set(val, q);
      } else if (this.#byValue.has(t)) {
        this.#order.push(t);
        this.#qty.set(t, 1);
      }
    }
  }

  /**
   * @param {string} v
   * @returns {number}
   */
  #getQty(v) {
    if (!this.#qtyEnabled) return 1;
    return this.#qty.get(v) ?? 1;
  }

  /**
   * @param {string} v
   * @returns {string}
   */
  #labelFor(v) {
    return this.#byValue.get(v)?.label ?? v;
  }

  /**
   * @param {string} v
   */
  #removeFromOrder(v) {
    const i = this.#order.indexOf(v);
    if (i >= 0) this.#order.splice(i, 1);
    this.#qty.delete(v);
  }

  /** @returns {number} */
  #totalQty() {
    let s = 0;
    for (const v of this.#order) {
      s += this.#getQty(v);
    }
    return s;
  }

  #qtyTotalMaxUnlimited() {
    return this.#qtyTotalMax <= 0;
  }

  /**
   * @param {string} v
   */
  #canIncreaseQty(v) {
    const q = this.#getQty(v);
    if (q >= this.#qtyMax) return false;
    if (this.#qtyTotalMaxUnlimited()) return true;
    return this.#totalQty() + 1 <= this.#qtyTotalMax;
  }

  #canAddNewOptionLine() {
    if (this.#qtyTotalMaxUnlimited()) return true;
    return this.#totalQty() + 1 <= this.#qtyTotalMax;
  }

  #clampQtyTotalMinToMax() {
    if (this.#qtyTotalMax > 0 && this.#qtyTotalMin > this.#qtyTotalMax) {
      this.#qtyTotalMin = this.#qtyTotalMax;
    }
  }

  #qtyMinMsg() {
    return this.#applyI18nCountTemplate(this.#i18n.qtyTotalMin, this.#qtyTotalMin);
  }

  #enforceTotalQtyCap() {
    if (this.#qtyTotalMaxUnlimited()) return;
    while (this.#totalQty() > this.#qtyTotalMax && this.#order.length > 0) {
      const v = this.#order[this.#order.length - 1];
      if (v === undefined) break;
      const q = this.#getQty(v);
      if (q > 1) {
        this.#qty.set(v, q - 1);
      } else {
        this.#removeFromOrder(v);
      }
    }
  }

  /** Authoritative limits from Liquid (avoids relying on host data-* parsing alone). */
  #readQtyRulesFromJson() {
    if (!this.#qtyEnabled) return;
    const el = this.querySelector('script[type="application/json"][data-pdp-cp-qty-rules]');
    if (!el?.textContent?.trim()) return;
    try {
      const j = JSON.parse(el.textContent.trim());
      const po = Number(j?.perOptionMax);
      const tm = Number(j?.totalMax);
      const tmin = Number(j?.totalMin);
      if (Number.isFinite(po) && po >= 2) {
        this.#qtyMax = Math.min(999, Math.max(2, Math.trunc(po)));
      }
      if (Number.isFinite(tm)) {
        this.#qtyTotalMax = tm >= 1 ? Math.min(9999, Math.trunc(tm)) : 0;
      }
      if (Number.isFinite(tmin) && tmin >= 1) {
        this.#qtyTotalMin = Math.min(9999, Math.trunc(tmin));
      }
    } catch {
      // keep attribute-based values
    }
  }

  /** Limits on the hidden input survive theme / CE edge cases better than host data-* alone. */
  #readQtyLimitsFromHidden() {
    if (!this.#qtyEnabled || !this.#hidden) return;
    const po = this.#hidden.getAttribute('data-pdp-cp-qty-per-option-max');
    const tm = this.#hidden.getAttribute('data-pdp-cp-qty-total-max');
    const tmin = this.#hidden.getAttribute('data-pdp-cp-qty-total-min');
    if (po != null && po !== '') {
      const n = Number.parseInt(po.trim(), 10);
      if (Number.isFinite(n) && n >= 2) {
        this.#qtyMax = Math.min(999, Math.max(2, n));
      }
    }
    if (tm != null && tm !== '') {
      const n = Number.parseInt(tm.trim(), 10);
      this.#qtyTotalMax = Number.isFinite(n) && n >= 1 ? Math.min(9999, n) : 0;
    }
    if (tmin != null && tmin !== '') {
      const n = Number.parseInt(tmin.trim(), 10);
      this.#qtyTotalMin = Number.isFinite(n) && n >= 1 ? Math.min(9999, n) : 0;
    }
  }

  /**
   * Multi-select Add button: optional (n/max) when quantity + total cap and user has selections.
   * @returns {string}
   */
  #addTriggerDisplayText() {
    let t = this.#i18n.add;
    if (!this.#single && this.#qtyEnabled && this.#order.length > 0) {
      if (this.#qtyTotalMax > 0) {
        const atTotalCap = !this.#canAddNewOptionLine();
        if (atTotalCap) {
          t = this.#i18n.change;
        } else {
          const remaining = this.#qtyTotalMax - this.#totalQty();
          const addPlural = this.#i18n.addColors || this.#i18n.add;
          t = remaining === 1 ? this.#i18n.add : addPlural;
        }
        t += ` (${this.#totalQty()}/${this.#qtyTotalMax})`;
      } else if (this.#qtyTotalMin > 0) {
        t += ` (${this.#totalQty()}/${this.#qtyTotalMin})`;
      }
    }
    return t;
  }

  #updateRowSelectionConstraints() {
    if (!this.#panelScroll || !this.#qtyEnabled || this.#single) return;
    const blockNew = !this.#canAddNewOptionLine();
    for (const btn of this.#panelScroll.querySelectorAll('[data-pdp-cp-row-value]')) {
      if (!(btn instanceof HTMLButtonElement)) continue;
      const v = btn.getAttribute('data-pdp-cp-row-value') ?? '';
      const selected = this.#order.includes(v);
      btn.disabled = !selected && blockNew;
      const line = btn.closest('.pdp-cp-select__row-line');
      const thumb = line?.querySelector('[data-pdp-cp-thumb-preview]');
      if (thumb instanceof HTMLButtonElement) thumb.disabled = btn.disabled;
    }
  }

  #refreshQtyDependentUi() {
    this.#updateRowQtyUi();
    this.#updateRowSelectionConstraints();
  }

  #buildPanelRows() {
    if (!this.#panelScroll) return;
    this.#hideThumbPreviewImmediate();
    this.#panelScroll.replaceChildren();

    const sorted = [...this.#options].sort((a, b) => {
      const c = this.#compareCategoryHeadings(a?.category, b?.category);
      if (c !== 0) return c;
      return (a.sortOrder ?? 0) - (b.sortOrder ?? 0);
    });

    let lastCat = /** @type {string | null} */ (null);
    /** @type {HTMLElement} */
    let appendTarget = this.#panelScroll;
    let groupSeq = 0;
    for (const opt of sorted) {
      const cat = (opt.category || '').trim();
      if (cat !== lastCat) {
        lastCat = cat;
        if (cat) {
          if (this.#collapsibleCategories) {
            groupSeq++;
            const baseId = `pdp-cp-cat-${this.#instanceUid()}-${groupSeq}`;
            const groupId = `${baseId}-group`;
            const btnId = `${baseId}-toggle`;
            const headBtn = document.createElement('button');
            headBtn.type = 'button';
            headBtn.id = btnId;
            headBtn.className = 'pdp-cp-select__category pdp-cp-select__category--toggle';
            headBtn.setAttribute('data-pdp-cp-cat-toggle', '');
            headBtn.setAttribute('aria-expanded', 'false');
            headBtn.setAttribute('aria-controls', groupId);
            headBtn.setAttribute('aria-label', this.#applyCategoryNameTemplate(this.#i18n.categoryToggle, cat));
            const headLabel = document.createElement('span');
            headLabel.className = 'pdp-cp-select__category-label';
            headLabel.textContent = cat;
            const headChevron = document.createElement('span');
            headChevron.className = 'pdp-cp-select__category-chevron';
            headChevron.setAttribute('aria-hidden', 'true');
            headBtn.append(headLabel, headChevron);
            this.#panelScroll.appendChild(headBtn);

            const group = document.createElement('div');
            group.id = groupId;
            group.className = 'pdp-cp-select__category-group';
            group.setAttribute('role', 'group');
            group.setAttribute('aria-labelledby', btnId);
            group.hidden = true;
            this.#panelScroll.appendChild(group);
            appendTarget = group;
          } else {
            const head = document.createElement('div');
            head.className = 'pdp-cp-select__category';
            head.textContent = cat;
            this.#panelScroll.appendChild(head);
            appendTarget = this.#panelScroll;
          }
        } else {
          appendTarget = this.#panelScroll;
        }
      }

      const line = document.createElement('div');
      line.className = 'pdp-cp-select__row-line';
      line.setAttribute('role', 'presentation');

      const labText = opt.label ?? String(opt.value);

      /** @type {HTMLElement} */
      let thumbSlot;
      if (opt.thumbUrl) {
        const thumbBtn = document.createElement('button');
        thumbBtn.type = 'button';
        thumbBtn.className = 'pdp-cp-select__row-thumb-btn';
        const previewSrc = (opt.thumbUrlLarge && String(opt.thumbUrlLarge).trim()) || opt.thumbUrl;
        thumbBtn.setAttribute('data-pdp-cp-thumb-preview', previewSrc);
        thumbBtn.setAttribute('aria-label', `${this.#i18n.thumbPreview}: ${labText}`);

        const img = document.createElement('img');
        img.className = 'pdp-cp-select__row-thumb';
        img.src = opt.thumbUrl;
        img.alt = '';
        img.width = 40;
        img.height = 40;
        img.loading = 'lazy';
        thumbBtn.appendChild(img);

        thumbBtn.addEventListener('pointerenter', this.#onThumbPreviewPointerEnter);
        thumbBtn.addEventListener('pointerleave', this.#onThumbPreviewPointerLeave);
        thumbBtn.addEventListener('pointerdown', this.#onThumbPreviewPointerDown);
        thumbBtn.addEventListener('focus', this.#onThumbPreviewFocus);
        thumbBtn.addEventListener('blur', this.#onThumbPreviewBlur);

        thumbSlot = thumbBtn;
      } else {
        const spacer = document.createElement('div');
        spacer.className = 'pdp-cp-select__row-thumb-spacer';
        spacer.setAttribute('aria-hidden', 'true');
        thumbSlot = spacer;
      }

      const row = document.createElement('button');
      row.type = 'button';
      row.className = 'pdp-cp-select__row';
      row.setAttribute('data-pdp-cp-row-value', String(opt.value));

      const lab = document.createElement('span');
      lab.className = 'pdp-cp-select__row-label';
      lab.textContent = labText;
      row.appendChild(lab);

      if (this.#qtyEnabled) {
        const qtyWrap = document.createElement('div');
        qtyWrap.className = 'pdp-cp-select__row-qty';
        qtyWrap.setAttribute('data-pdp-cp-row-qty', '');
        qtyWrap.hidden = true;

        const dec = document.createElement('button');
        dec.type = 'button';
        dec.className = 'pdp-cp-select__row-qty-btn';
        dec.setAttribute('data-pdp-cp-qty-dec', '');
        dec.textContent = '−';

        const readout = document.createElement('span');
        readout.className = 'pdp-cp-select__row-qty-readout';
        readout.setAttribute('data-pdp-cp-qty-readout', '');
        readout.setAttribute('aria-live', 'polite');

        const inc = document.createElement('button');
        inc.type = 'button';
        inc.className = 'pdp-cp-select__row-qty-btn';
        inc.setAttribute('data-pdp-cp-qty-inc', '');
        inc.textContent = '+';

        qtyWrap.append(dec, readout, inc);
        line.append(thumbSlot, row, qtyWrap);
      } else {
        line.append(thumbSlot, row);
      }

      appendTarget.appendChild(line);
    }
  }

  #applyCategoryNameTemplate(tpl, name) {
    if (typeof tpl !== 'string') return name;
    const s = String(name);
    return tpl.replace(/\{name\}/g, s).replace(/\{\{\s*name\s*\}\}/g, s);
  }

  #instanceUid() {
    if (!this.__pdpCpUid) {
      this.__pdpCpUid = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
    }
    return this.__pdpCpUid;
  }

  #clearThumbPreviewHideTimer() {
    if (this.#thumbPreviewHideTimer != null) {
      clearTimeout(this.#thumbPreviewHideTimer);
      this.#thumbPreviewHideTimer = null;
    }
  }

  /** @param {number} [delay] */
  #scheduleHideThumbPreview(delay = 180) {
    this.#clearThumbPreviewHideTimer();
    this.#thumbPreviewHideTimer = setTimeout(() => {
      this.#thumbPreviewHideTimer = null;
      this.#hideThumbPreviewImmediate();
    }, delay);
  }

  #hideThumbPreviewImmediate() {
    this.#clearThumbPreviewHideTimer();
    this.#thumbPreviewAnchor = null;
    const p = this.#thumbPreviewPopover;
    if (p?.wrap) {
      p.wrap.hidden = true;
      p.wrap.classList.remove('pdp-cp-select__thumb-preview-popover--visible');
    }
    window.removeEventListener('scroll', this.#onThumbPreviewScrollResize, true);
    window.removeEventListener('resize', this.#onThumbPreviewScrollResize);
  }

  #destroyThumbPreviewPopover() {
    this.#hideThumbPreviewImmediate();
    const w = this.#thumbPreviewPopover?.wrap;
    if (w?.parentNode) w.remove();
    this.#thumbPreviewPopover = null;
  }

  #onThumbPreviewScrollResize = () => {
    this.#hideThumbPreviewImmediate();
  };

  #getOrCreateThumbPreviewPopover() {
    if (this.#thumbPreviewPopover) return this.#thumbPreviewPopover;
    const wrap = document.createElement('div');
    wrap.className = 'pdp-cp-select__thumb-preview-popover';
    wrap.hidden = true;
    wrap.setAttribute('aria-hidden', 'true');
    const img = document.createElement('img');
    img.alt = '';
    img.className = 'pdp-cp-select__thumb-preview-popover-img';
    wrap.appendChild(img);
    wrap.addEventListener('pointerenter', () => this.#clearThumbPreviewHideTimer());
    wrap.addEventListener('pointerleave', () => this.#scheduleHideThumbPreview());
    document.body.appendChild(wrap);
    this.#thumbPreviewPopover = { wrap, img };
    return this.#thumbPreviewPopover;
  }

  /**
   * @param {string} src
   * @param {HTMLElement} anchor
   */
  #showThumbPreview(src, anchor) {
    if (!src || !this.#isOpen()) return;
    const { wrap, img } = this.#getOrCreateThumbPreviewPopover();
    this.#clearThumbPreviewHideTimer();
    this.#thumbPreviewAnchor = anchor;

    const reposition = () => {
      if (this.#thumbPreviewAnchor === anchor && anchor.isConnected) this.#positionThumbPreview(anchor);
    };

    if (img.src !== src) {
      img.src = src;
      img.addEventListener('load', reposition, { once: true });
    }

    wrap.hidden = false;
    requestAnimationFrame(() => {
      reposition();
      wrap.classList.add('pdp-cp-select__thumb-preview-popover--visible');
    });

    window.removeEventListener('scroll', this.#onThumbPreviewScrollResize, true);
    window.removeEventListener('resize', this.#onThumbPreviewScrollResize);
    window.addEventListener('scroll', this.#onThumbPreviewScrollResize, true);
    window.addEventListener('resize', this.#onThumbPreviewScrollResize);
  }

  /**
   * @param {HTMLElement} anchor
   */
  #positionThumbPreview(anchor) {
    const wrap = this.#thumbPreviewPopover?.wrap;
    if (!wrap || !anchor.isConnected) return;

    const ar = anchor.getBoundingClientRect();
    const margin = 8;
    wrap.style.visibility = 'hidden';
    const pr = wrap.getBoundingClientRect();

    let top = ar.top - pr.height - margin;
    if (top < margin) top = ar.bottom + margin;

    let left = ar.left + ar.width / 2 - pr.width / 2;
    const maxLeft = window.innerWidth - pr.width - margin;
    left = Math.max(margin, Math.min(left, maxLeft));

    wrap.style.top = `${Math.round(top)}px`;
    wrap.style.left = `${Math.round(left)}px`;
    wrap.style.visibility = '';
  }

  /** @param {PointerEvent} e */
  #onThumbPreviewPointerEnter = (e) => {
    const btn = e.currentTarget;
    if (!(btn instanceof HTMLButtonElement) || btn.disabled) return;
    if (e.pointerType === 'touch') return;
    const src = btn.getAttribute('data-pdp-cp-thumb-preview');
    if (!src) return;
    this.#showThumbPreview(src, btn);
  };

  /** @param {PointerEvent} e */
  #onThumbPreviewPointerLeave = (e) => {
    if (e.pointerType === 'touch') return;
    const rel = e.relatedTarget;
    const pop = this.#thumbPreviewPopover?.wrap;
    if (rel instanceof Node && pop?.contains(rel)) return;
    this.#scheduleHideThumbPreview();
  };

  /** @param {PointerEvent} e */
  #onThumbPreviewPointerDown = (e) => {
    const btn = e.currentTarget;
    if (!(btn instanceof HTMLButtonElement) || btn.disabled) return;
    if (e.button !== 0) return;
    const src = btn.getAttribute('data-pdp-cp-thumb-preview');
    if (!src) return;

    if (e.pointerType === 'touch' || e.pointerType === 'pen') {
      this.#showThumbPreview(src, btn);
      const pid = e.pointerId;
      /** @param {PointerEvent} ev */
      const end = (ev) => {
        if (ev.pointerId !== pid) return;
        document.removeEventListener('pointerup', end, true);
        document.removeEventListener('pointercancel', end, true);
        this.#scheduleHideThumbPreview(100);
      };
      document.addEventListener('pointerup', end, true);
      document.addEventListener('pointercancel', end, true);
    }
  };

  /** @param {FocusEvent} e */
  #onThumbPreviewFocus = (e) => {
    const btn = e.currentTarget;
    if (!(btn instanceof HTMLButtonElement) || btn.disabled) return;
    const src = btn.getAttribute('data-pdp-cp-thumb-preview');
    if (!src) return;
    this.#showThumbPreview(src, btn);
  };

  /** @param {FocusEvent} e */
  #onThumbPreviewBlur = (e) => {
    const rel = e.relatedTarget;
    const pop = this.#thumbPreviewPopover?.wrap;
    if (rel instanceof Node && pop?.contains(rel)) return;
    this.#scheduleHideThumbPreview();
  };

  #clearPillNodes() {
    if (!this.#pills || !this.#trigger) return;
    for (const node of [...this.#pills.childNodes]) {
      if (node !== this.#trigger) node.remove();
    }
  }

  /**
   * @param {string} val
   * @returns {string}
   */
  #pillDisplayText(val) {
    const label = this.#labelFor(val);
    if (!this.#qtyEnabled) return label;
    const q = this.#getQty(val);
    return `${q} x ${label}`;
  }

  #renderPills() {
    if (!this.#pills || !this.#trigger) return;
    this.#clearPillNodes();
    if (this.#single) return;

    for (const val of this.#order) {
      const label = this.#labelFor(val);
      const shown = this.#pillDisplayText(val);

      const pill = document.createElement('button');
      pill.type = 'button';
      pill.className = 'pills__pill pdp-cp-select__pill';
      pill.setAttribute('data-pdp-cp-pill-value', val);
      pill.setAttribute('aria-label', `${this.#i18n.remove} ${label}`);
      pill.textContent = shown;

      const svg = document.createElement('span');
      svg.className = 'svg-wrapper svg-wrapper--smaller';
      svg.innerHTML = `
        <svg width="7" height="8" viewBox="0 0 7 8" fill="none" xmlns="http://www.w3.org/2000/svg">
          <g opacity="var(--close-icon-opacity)">
          <path d="M6 1.5L1 6.5" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round"/>
          <path d="M1 1.5L6 6.5" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round"/>
          </g>
        </svg>
      `;
      pill.appendChild(svg);

      this.#pills.insertBefore(pill, this.#trigger);
    }
  }

  #updateChrome() {
    const has = this.#order.length > 0;

    if (this.#single) {
      this.#body?.classList.remove('pdp-cp-select__body--has-pills');
      if (this.#placeholderEl) {
        this.#placeholderEl.hidden = has;
        if (!has) this.#placeholderEl.textContent = this.#placeholderText;
      }
      if (this.#selectedLabel) {
        if (has) {
          const val = this.#order[0];
          this.#selectedLabel.textContent = val !== undefined ? this.#pillDisplayText(val) : '';
          this.#selectedLabel.hidden = false;
        } else {
          this.#selectedLabel.textContent = '';
          this.#selectedLabel.hidden = true;
        }
      }
      if (this.#trigger) this.#trigger.hidden = true;
      return;
    }

    this.#body?.classList.toggle('pdp-cp-select__body--has-pills', has);

    if (this.#placeholderEl) {
      this.#placeholderEl.hidden = has;
      if (!has) this.#placeholderEl.textContent = this.#placeholderText;
    }
    if (this.#selectedLabel) {
      this.#selectedLabel.textContent = '';
      this.#selectedLabel.hidden = true;
    }
    if (this.#trigger) {
      this.#trigger.hidden = !has;
      this.#trigger.textContent = this.#addTriggerDisplayText();
      this.#trigger.disabled = false;
    }
  }

  #syncHiddenFromSelected() {
    if (!this.#hidden) return;
    if (this.#qtyEnabled && this.#qtyTotalMax > 0) {
      this.#enforceTotalQtyCap();
    }
    if (this.#qtyEnabled) {
      const parts = this.#order.map((v) => {
        const q = this.#getQty(v);
        return `${q} x ${this.#labelFor(v)}`;
      });
      this.#hidden.value = this.#single ? (parts[0] ?? '') : parts.join(this.#separator);
    } else {
      this.#hidden.value = this.#single ? (this.#order[0] ?? '') : this.#order.join(this.#separator);
    }
    if (this.dataset.presetPicker === 'true') {
      this.dispatchEvent(new Event('change', { bubbles: true }));
    } else {
      this.#hidden.dispatchEvent(new Event('input', { bubbles: true }));
    }
  }

  #updateRowPressedStates() {
    if (this.#panelScroll) {
      for (const btn of this.#panelScroll.querySelectorAll('[data-pdp-cp-row-value]')) {
        if (!(btn instanceof HTMLButtonElement)) continue;
        const v = btn.getAttribute('data-pdp-cp-row-value') ?? '';
        btn.setAttribute('aria-pressed', this.#order.includes(v) ? 'true' : 'false');
      }
    }
    this.#syncColorChartGridSelection();
  }

  #updateRowQtyUi() {
    if (this.#panelScroll && this.#qtyEnabled) {
      for (const btn of this.#panelScroll.querySelectorAll('[data-pdp-cp-row-value]')) {
        if (!(btn instanceof HTMLButtonElement)) continue;
        const v = btn.getAttribute('data-pdp-cp-row-value') ?? '';
        const line = btn.closest('.pdp-cp-select__row-line');
        const wrap = line?.querySelector('[data-pdp-cp-row-qty]');
        if (!(wrap instanceof HTMLElement)) continue;
        const selected = this.#order.includes(v);
        wrap.hidden = !selected;
        if (!selected) continue;
        const q = this.#getQty(v);
        const lab = this.#labelFor(v);
        const readout = wrap.querySelector('[data-pdp-cp-qty-readout]');
        const dec = wrap.querySelector('[data-pdp-cp-qty-dec]');
        const inc = wrap.querySelector('[data-pdp-cp-qty-inc]');
        if (readout) readout.textContent = String(q);
        if (dec instanceof HTMLButtonElement) {
          dec.disabled = false;
          dec.setAttribute('aria-label', `${this.#i18n.qtyDecrease}, ${lab}`);
        }
        if (inc instanceof HTMLButtonElement) {
          inc.disabled = !this.#canIncreaseQty(v);
          inc.setAttribute('aria-label', `${this.#i18n.qtyIncrease}, ${lab}`);
        }
      }
    }
    this.#syncColorChartGridQtyUI();
  }

  #lineQtyMinSatisfied() {
    if (!this.#qtyEnabled || this.#qtyTotalMin <= 0) return true;
    if (this.#order.length === 0) return true;
    return this.#totalQty() >= this.#qtyTotalMin;
  }

  #updateValidity() {
    if (!this.#hidden) return;
    if (this.#required) {
      if (this.#order.length === 0) {
        this.#hidden.setCustomValidity(this.#i18n.required || ' ');
        return;
      }
      if (!this.#lineQtyMinSatisfied()) {
        this.#hidden.setCustomValidity(this.#qtyMinMsg() || ' ');
        return;
      }
      this.#hidden.setCustomValidity('');
      return;
    }
    if (!this.#lineQtyMinSatisfied()) {
      this.#hidden.setCustomValidity(this.#qtyMinMsg() || ' ');
    } else {
      this.#hidden.setCustomValidity('');
    }
  }

  #isOpen() {
    return this.#panel && !this.#panel.hasAttribute('hidden');
  }

  #setExpanded(open) {
    const v = open ? 'true' : 'false';
    this.#face?.setAttribute('aria-expanded', v);
  }

  #openPanel() {
    if (!this.#panel) return;
    this.#panel.removeAttribute('hidden');
    this.#setExpanded(true);
    this.#updateChrome();
    this.#refreshQtyDependentUi();
    requestAnimationFrame(() => this.#updatePanelScrollTopFade());
  }

  #closePanel() {
    this.#hideThumbPreviewImmediate();
    if (!this.#panel) return;
    this.#panel.setAttribute('hidden', '');
    this.#panel.classList.remove('pdp-cp-select__panel--scrolled');
    this.#setExpanded(false);
  }

  #togglePanel() {
    if (this.#isOpen()) this.#closePanel();
    else this.#openPanel();
  }

  /** @param {MouseEvent} e */
  #handleFaceClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    this.#togglePanel();
  };

  /** @param {MouseEvent} e */
  #handleTriggerClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    this.#togglePanel();
  };

  /**
   * Clicking the bar outside interactive controls (gutter, empty pills strip, padding) opens the panel.
   * Multi-select: pills keep remove behaviour; face and Add use their own handlers (stopPropagation).
   * Single-select: same for row chrome; face click still toggles via #handleFaceClick.
   * @param {MouseEvent} e
   */
  #handleSelectBarClick = (e) => {
    const t = e.target;
    if (!(t instanceof Element)) return;
    if (t.closest('[data-pdp-cp-pill-value]')) return;
    if (this.#trigger?.contains(t)) return;
    if (this.#face?.contains(t)) return;
    if (this.#isOpen()) return;
    this.#openPanel();
  };

  /** @param {MouseEvent} e */
  #handlePanelClick = (e) => {
    const t = e.target;
    if (!(t instanceof Element)) return;

    const catToggle = t.closest('[data-pdp-cp-cat-toggle]');
    if (catToggle && this.#panelScroll?.contains(catToggle)) {
      e.preventDefault();
      e.stopPropagation();
      const groupId = catToggle.getAttribute('aria-controls');
      const group = groupId ? this.querySelector(`#${CSS.escape(groupId)}`) : null;
      if (group instanceof HTMLElement) {
        const expanded = catToggle.getAttribute('aria-expanded') === 'true';
        catToggle.setAttribute('aria-expanded', expanded ? 'false' : 'true');
        group.hidden = expanded;
      }
      return;
    }

    const inc = t.closest('[data-pdp-cp-qty-inc]');
    const dec = t.closest('[data-pdp-cp-qty-dec]');
    if (inc || dec) {
      e.preventDefault();
      e.stopPropagation();
      const ctrl = inc instanceof HTMLButtonElement ? inc : dec instanceof HTMLButtonElement ? dec : null;
      if (!ctrl || !this.#qtyEnabled) return;
      const wrap = ctrl.closest('[data-pdp-cp-row-qty]');
      const line = wrap?.closest('.pdp-cp-select__row-line');
      const rowBtn = line?.querySelector('[data-pdp-cp-row-value]');
      const v = rowBtn?.getAttribute('data-pdp-cp-row-value');
      if (v == null) return;

      const q = this.#getQty(v);
      if (inc) {
        if (this.#canIncreaseQty(v)) this.#qty.set(v, q + 1);
      } else if (q <= 1) {
        this.#removeFromOrder(v);
      } else {
        this.#qty.set(v, q - 1);
      }

      this.#syncHiddenFromSelected();
      this.#renderPills();
      this.#updateChrome();
      this.#updateRowPressedStates();
      this.#refreshQtyDependentUi();
      this.#updateValidity();
      return;
    }

    const row = t.closest('[data-pdp-cp-row-value]');
    if (!row || !(row instanceof HTMLButtonElement)) return;
    if (!this.#panelScroll?.contains(row)) return;

    const v = row.getAttribute('data-pdp-cp-row-value');
    if (v == null) return;

    if (this.#single) {
      this.#applySingleChoiceValue(v, { closePanel: true, closeDialog: false });
      return;
    }

    const i = this.#order.indexOf(v);
    if (i >= 0) {
      this.#removeFromOrder(v);
    } else {
      if (!this.#canAddNewOptionLine()) return;
      this.#order.push(v);
      if (this.#qtyEnabled) this.#qty.set(v, 1);
    }

    this.#syncHiddenFromSelected();
    this.#renderPills();
    this.#updateChrome();
    this.#updateRowPressedStates();
    this.#refreshQtyDependentUi();
    this.#updateValidity();
  };

  /** @param {MouseEvent} e */
  #handlePillsStripClick = (e) => {
    if (this.#single) return;
    const t = e.target;
    if (!(t instanceof Node)) return;
    if (this.#trigger?.contains(t)) return;

    const pill = t instanceof Element ? t.closest('[data-pdp-cp-pill-value]') : null;
    if (!(pill instanceof HTMLButtonElement)) return;

    e.preventDefault();
    e.stopPropagation();
    const v = pill.getAttribute('data-pdp-cp-pill-value');
    if (v == null) return;
    this.#removeFromOrder(v);
    this.#syncHiddenFromSelected();
    this.#renderPills();
    this.#updateChrome();
    this.#updateRowPressedStates();
    this.#refreshQtyDependentUi();
    this.#updateValidity();
  };

  /** @param {MouseEvent} e */
  #onDocClick = (e) => {
    if (!this.#isOpen()) return;
    const t = e.target;
    if (t instanceof Node && this.contains(t)) return;
    if (t instanceof Node && this.#thumbPreviewPopover?.wrap?.contains(t)) return;
    this.#closePanel();
  };

  /** @param {KeyboardEvent} e */
  #onKeydown = (e) => {
    if (e.key !== 'Escape' || !this.#isOpen()) return;
    this.#closePanel();
  };

  /** @param {SubmitEvent} e */
  #onFormSubmit = (e) => {
    this.#updateValidity();
    if (this.#hidden && !this.#hidden.checkValidity()) {
      e.preventDefault();
      this.#hidden.reportValidity();
    }
  };

  /**
   * Thumbnail URLs for section header swatches (one entry per line item when per-option quantity is on).
   * @returns {string[]}
   */
  getSelectionThumbUrls() {
    const urls = /** @type {string[]} */ ([]);
    if (this.#qtyEnabled) {
      for (const v of this.#order) {
        const q = this.#getQty(v);
        const o = this.#byValue.get(v);
        const u = o?.thumbUrl != null && String(o.thumbUrl).trim() !== '' ? String(o.thumbUrl).trim() : '';
        const n = Math.min(99, Math.max(1, q));
        for (let i = 0; i < n; i += 1) {
          urls.push(u);
        }
      }
    } else {
      for (const v of this.#order) {
        const o = this.#byValue.get(v);
        urls.push(o?.thumbUrl != null && String(o.thumbUrl).trim() !== '' ? String(o.thumbUrl).trim() : '');
      }
    }
    return urls;
  }

  /**
   * @public — used by `pdp-custom-properties` add-to-cart gate (optional fields with a total min).
   * @returns {boolean}
   */
  pdpLineQtyMinSatisfied() {
    return this.#lineQtyMinSatisfied();
  }
}

customElements.define('pdp-cp-custom-select', PdpCpCustomSelect);

/**
 * Applies color-scheme presets to line-item property inputs when the preset dropdown changes.
 *
 * @extends {Component<Record<string, never>>}
 */
class PdpCustomProperties extends Component {
  /** @type {{ title: string; mappings: { internalKey: string; value: string; quantity?: number }[] }[] } */
  #presets = [];
  /** When true, field updates come from applying a preset — skip preset invalidation. */
  #applyingPreset = false;
  /** @type {{ addAria: string }} */
  #cartAddonI18n = {
    addAria: 'Add [[TITLE]] for [[PRICE]] to cart',
  };
  /** @type {Set<HTMLElement>} groups (one addon product each) currently selected */
  #selectedAddonGroups = new Set();
  /** @type {(() => void) | undefined} */
  #teardownCartAddonCarouselFades;
  /** @type {AbortController | null} */
  #pdpAddToCartGateAbort = null;
  /** @type {string[]} ordered, lowercased product option names (from data-pdp-cp-product-options) */
  #productOptionNames = [];

  /**
   * HTML5 omits `type=hidden` from constraint validation, so `form.checkValidity()` does not
   * enforce our required line-item `hidden` inputs. Match Liquid `required` + a non-empty value.
   * @returns {boolean}
   */
  #pdpRequiredLineItemContentOk() {
    for (const sel of this.querySelectorAll('pdp-cp-custom-select[data-required="true"]')) {
      if (!(sel instanceof HTMLElement) || sel.closest('.pdp-cp__cart-addons') || sel.dataset.presetPicker === 'true') {
        continue;
      }
      if (sel.closest('[data-pdp-cp-conditional][hidden]')) continue;
      const h = sel.querySelector('input[type="hidden"][data-pdp-cp-value-input]');
      if (!(h instanceof HTMLInputElement)) return false;
      if (!(h.value || '').trim()) return false;
    }
    for (const el of this.querySelectorAll('textarea[required][data-pdp-cp-property], input[required][data-pdp-cp-property]')) {
      if (el.getAttribute('type') === 'hidden') continue;
      if (el.closest('[data-pdp-cp-conditional][hidden]')) continue;
      if (el instanceof HTMLTextAreaElement || el instanceof HTMLInputElement) {
        if (!(el.value || '').trim()) return false;
      }
    }
    return true;
  }

  /**
   * Per-option quantity fields with a total min (e.g. must pick 3/3) — not covered by `required` alone.
   * @returns {boolean}
   */
  #pdpQtyLineTotalsOk() {
    for (const sel of this.querySelectorAll('pdp-cp-custom-select')) {
      if (!(sel instanceof HTMLElement) || sel.closest('.pdp-cp__cart-addons') || sel.dataset.presetPicker === 'true') {
        continue;
      }
      if (sel.closest('[data-pdp-cp-conditional][hidden]')) continue;
      const gate = /** @type {{ pdpLineQtyMinSatisfied?: () => boolean }} */ (sel);
      if (typeof gate.pdpLineQtyMinSatisfied === 'function' && !gate.pdpLineQtyMinSatisfied()) {
        return false;
      }
    }
    return true;
  }

  /**
   * @public — used by `product-form` and `#syncPdpAddToCartGuard` (hidden inputs skip native validation).
   * @returns {boolean}
   */
  pdpLineItemsSatisfied() {
    if (!this.#pdpRequiredLineItemContentOk()) return false;
    if (!this.#pdpQtyLineTotalsOk()) return false;
    return true;
  }

  /**
   * PDP custom properties usually render in a separate block from buy-buttons, so the component is
   * not a descendant of `form`. Fields use the `form="..."` attribute; we resolve the form here.
   * @returns {{ pfc: HTMLElement; form: HTMLFormElement } | null}
   */
  #getPdpProductFormContext() {
    const id = (this.dataset.productId || '').trim();
    if (id) {
      const pfc = document.querySelector(`product-form-component[data-product-id="${CSS.escape(id)}"]`);
      const form =
        pfc?.querySelector('form[data-type="add-to-cart-form"]') ?? pfc?.querySelector('form') ?? null;
      if (pfc instanceof HTMLElement && form instanceof HTMLFormElement) {
        return { pfc, form };
      }
    }
    const section = this.closest('.shopify-section');
    const pfc = section?.querySelector('product-form-component') ?? null;
    const form =
      pfc?.querySelector('form[data-type="add-to-cart-form"]') ?? pfc?.querySelector('form') ?? null;
    if (pfc instanceof HTMLElement && form instanceof HTMLFormElement) {
      return { pfc, form };
    }
    return null;
  }

  /**
   * Toggle conditional field wrappers based on the selected variant's option values.
   * @param {unknown} variantOptionValues - e.detail.resource.options (array of option values)
   */
  #syncConditionalFields = (variantOptionValues) => {
    const values = Array.isArray(variantOptionValues) ? variantOptionValues : [];
    const pairSet = buildVariantPairSet(this.#productOptionNames, values);
    for (const wrapper of this.querySelectorAll('[data-pdp-cp-conditional]')) {
      if (!(wrapper instanceof HTMLElement)) continue;
      let conditions = [];
      try {
        const parsed = JSON.parse(wrapper.dataset.pdpCpConditions || '[]');
        if (Array.isArray(parsed)) conditions = parsed;
      } catch {
        conditions = [];
      }
      const show = fieldMatches(conditions, pairSet);
      wrapper.toggleAttribute('hidden', !show);
      for (const input of wrapper.querySelectorAll('input[name], textarea[name]')) {
        if (input instanceof HTMLInputElement || input instanceof HTMLTextAreaElement) {
          input.disabled = !show;
        }
      }
    }
  };

  /**
   * Locate this product's variant picker. The picker is a sibling block of the buy-buttons form (not a
   * descendant of `product-form-component`), so it is resolved from the section, scoped by product id.
   * @returns {HTMLElement | null}
   */
  #findVariantPicker = () => {
    const id = (this.dataset.productId || '').trim();
    const scope = this.closest('.shopify-section, dialog, product-card') ?? document;
    let picker = id ? scope.querySelector(`variant-picker[data-product-id="${CSS.escape(id)}"]`) : null;
    if (!picker) picker = scope.querySelector('variant-picker');
    return picker instanceof HTMLElement ? picker : null;
  };

  /**
   * Synchronously read the selected variant option values from the variant picker, in product-option
   * order. Used to toggle conditional fields instantly on option change — `variant:update` only fires
   * after the picker fetches the section HTML, which is too slow for show/hide.
   * @returns {string[] | null} option values index-aligned to product options, or null if no picker
   */
  #readVariantOptionValues = () => {
    const picker = this.#findVariantPicker();
    if (!picker) return null;
    const fieldsets = picker.querySelectorAll('fieldset[data-fieldset-index]');
    if (fieldsets.length) {
      /** @type {string[]} */
      const values = [];
      for (const fs of fieldsets) {
        if (!(fs instanceof HTMLElement)) continue;
        const index = Number.parseInt(fs.dataset.fieldsetIndex || '', 10);
        if (Number.isNaN(index)) continue;
        const checked = fs.querySelector('input:checked');
        values[index] = checked instanceof HTMLInputElement ? checked.value : '';
      }
      return values;
    }
    const selects = picker.querySelectorAll('select[name^="options["]');
    if (selects.length) {
      return Array.from(selects, (s) => (s instanceof HTMLSelectElement ? s.value : ''));
    }
    return null;
  };

  #syncPdpAddToCartGuard = () => {
    const ctx = this.#getPdpProductFormContext();
    if (!ctx) return;
    const { pfc } = ctx;
    const pdpOk = this.pdpLineItemsSatisfied();
    for (const c of pfc.querySelectorAll('add-to-cart-component')) {
      const btn = c.querySelector('button[name="add"]');
      if (!(btn instanceof HTMLButtonElement)) continue;
      const variantAllow = btn.getAttribute('data-variant-allow') !== 'false';
      if (!pdpOk) {
        btn.setAttribute('data-pdp-gate', '');
        btn.disabled = true;
      } else {
        btn.removeAttribute('data-pdp-gate');
        btn.disabled = !variantAllow;
      }
    }
  };

  #bindPdpAddToCartGate = () => {
    const ctx = this.#getPdpProductFormContext();
    if (!ctx) return;
    const { form } = ctx;
    const section = this.closest('.shopify-section, dialog, product-card');
    this.#pdpAddToCartGateAbort?.abort();
    this.#pdpAddToCartGateAbort = new AbortController();
    const { signal } = this.#pdpAddToCartGateAbort;
    try {
      const parsed = JSON.parse(this.dataset.pdpCpProductOptions || '[]');
      this.#productOptionNames = Array.isArray(parsed) ? parsed : [];
    } catch {
      this.#productOptionNames = [];
    }
    // Controls associated via `form="id"` are not DOM descendants — events do not reach `form` on bubble.
    form.addEventListener('input', this.#syncPdpAddToCartGuard, { signal, capture: true });
    form.addEventListener('change', this.#syncPdpAddToCartGuard, { signal, capture: true });
    this.addEventListener('input', this.#syncPdpAddToCartGuard, { signal, capture: true });
    this.addEventListener('change', this.#syncPdpAddToCartGuard, { signal, capture: true });
    // Fast path: toggle conditional fields the instant a variant option changes, before the picker's
    // `variant:update` (which awaits a section fetch). The picker is a sibling block, so listen on the
    // section (which contains it and survives inner morphs). `variant:update` below re-syncs authoritatively.
    const variantChangeTarget = section ?? document;
    variantChangeTarget.addEventListener(
      'change',
      (event) => {
        const target = event.target;
        if (!(target instanceof Element) || !target.closest('variant-picker')) return;
        const values = this.#readVariantOptionValues();
        if (values) this.#syncConditionalFields(values);
      },
      { signal, capture: true }
    );
    section?.addEventListener(
      ThemeEvents.variantUpdate,
      (event) => {
        const detail = /** @type {{ resource?: { options?: unknown } }} */ (
          /** @type {CustomEvent} */ (event).detail
        );
        this.#syncConditionalFields(detail?.resource?.options);
        this.#syncPdpAddToCartGuard();
      },
      { signal }
    );
    requestAnimationFrame(() => this.#syncPdpAddToCartGuard());
  };

  #syncHeaderSelectionSwatches = () => {
    const ul = this.querySelector('[data-pdp-cp-header-swatches]');
    if (!(ul instanceof HTMLUListElement)) return;
    ul.replaceChildren();
    for (const el of this.querySelectorAll('pdp-cp-custom-select')) {
      if (!(el instanceof HTMLElement) || !this.contains(el)) continue;
      if (el.closest('.pdp-cp__cart-addons')) continue;
      if (el.dataset.pdpCpColorChart !== 'true') continue;
      const fn = /** @type {{ getSelectionThumbUrls?: () => string[] }} */ (el).getSelectionThumbUrls;
      if (typeof fn !== 'function') continue;
      const thumbs = fn.call(el);
      if (!Array.isArray(thumbs)) continue;
      for (const url of thumbs) {
        const li = document.createElement('li');
        li.className = 'pdp-cp__header-swatch';
        if (url) {
          const img = document.createElement('img');
          img.className = 'pdp-cp__header-swatch-img';
          img.src = url;
          img.alt = '';
          img.width = 28;
          img.height = 28;
          img.loading = 'lazy';
          li.appendChild(img);
        } else {
          li.classList.add('pdp-cp__header-swatch--empty');
          li.setAttribute('aria-hidden', 'true');
        }
        ul.appendChild(li);
      }
    }
    ul.toggleAttribute('hidden', ul.childElementCount === 0);
  };

  connectedCallback() {
    super.connectedCallback();
    const script = this.querySelector('script[type="application/json"][data-pdp-cp-presets]');
    if (script?.textContent) {
      try {
        const parsed = JSON.parse(script.textContent.trim());
        this.#presets = Array.isArray(parsed) ? parsed : [];
      } catch {
        this.#presets = [];
      }
    }
    const i18nScript = this.querySelector('script[type="application/json"][data-pdp-cp-cart-addon-i18n]');
    if (i18nScript?.textContent) {
      try {
        const parsed = JSON.parse(i18nScript.textContent.trim());
        if (parsed && typeof parsed === 'object') {
          if (typeof parsed.addAria === 'string') this.#cartAddonI18n.addAria = parsed.addAria;
        }
      } catch {
        // keep defaults
      }
    }
    this.addEventListener('change', this.#onChange);
    this.addEventListener('change', this.#onCartAddonVariantChange);
    this.addEventListener('input', this.#onGlobalCartAddonInput, true);
    this.addEventListener('change', this.#onDescendantMaybeInvalidatePreset, true);
    this.addEventListener('input', this.#onDescendantMaybeInvalidatePreset, true);
    this.addEventListener('click', this.#onCartAddonClick);
    this.addEventListener('input', this.#syncHeaderSelectionSwatches, true);
    this.addEventListener('change', this.#syncHeaderSelectionSwatches, true);
    this.#teardownCartAddonCarouselFades = this.#initCartAddonCarouselFades();
    requestAnimationFrame(() => {
      for (const r of this.querySelectorAll('.pdp-cp__cart-addons')) {
        this.#syncAllCartAddonGroupsInRegion(/** @type {HTMLElement} */(r));
      }
      this.#syncHeaderSelectionSwatches();
      this.#bindPdpAddToCartGate();
    });
  }

  disconnectedCallback() {
    this.#pdpAddToCartGateAbort?.abort();
    this.#pdpAddToCartGateAbort = null;
    this.#teardownCartAddonCarouselFades?.();
    this.#teardownCartAddonCarouselFades = undefined;
    this.removeEventListener('change', this.#onChange);
    this.removeEventListener('change', this.#onCartAddonVariantChange);
    this.removeEventListener('input', this.#onGlobalCartAddonInput, true);
    this.removeEventListener('change', this.#onDescendantMaybeInvalidatePreset, true);
    this.removeEventListener('input', this.#onDescendantMaybeInvalidatePreset, true);
    this.removeEventListener('click', this.#onCartAddonClick);
    this.removeEventListener('input', this.#syncHeaderSelectionSwatches, true);
    this.removeEventListener('change', this.#syncHeaderSelectionSwatches, true);
    super.disconnectedCallback();
  }

  /**
   * Edge fade hints on the cart-addons slideshow; toggles data attributes for CSS.
   * @returns {() => void}
   */
  #initCartAddonCarouselFades() {
    const root = this.querySelector('.pdp-cp__cart-addons-carousel');
    const scroller = root?.querySelector('slideshow-slides');
    if (!(root instanceof HTMLElement) || !(scroller instanceof HTMLElement)) {
      return () => { };
    }

    const SYNC_TOLERANCE = 2;

    const update = () => {
      const overflow = scroller.scrollWidth > scroller.clientWidth + 1;
      if (!overflow) {
        root.removeAttribute('data-pdp-cp-scroll-overflow');
        root.removeAttribute('data-pdp-cp-scroll-start');
        root.removeAttribute('data-pdp-cp-scroll-end');
        return;
      }

      root.setAttribute('data-pdp-cp-scroll-overflow', '');
      const atStart = scroller.scrollLeft <= SYNC_TOLERANCE;
      const atEnd =
        scroller.scrollLeft + scroller.clientWidth >= scroller.scrollWidth - SYNC_TOLERANCE;

      if (atStart) {
        root.setAttribute('data-pdp-cp-scroll-start', '');
      } else {
        root.removeAttribute('data-pdp-cp-scroll-start');
      }

      if (atEnd) {
        root.setAttribute('data-pdp-cp-scroll-end', '');
      } else {
        root.removeAttribute('data-pdp-cp-scroll-end');
      }
    };

    scroller.addEventListener('scroll', update, { passive: true });
    const ro = new ResizeObserver(update);
    ro.observe(scroller);

    const slideshow = root.querySelector('slideshow-component');
    /** @param {MutationRecord[]} records */
    const onSlideshowAttrs = (records) => {
      for (const r of records) {
        if (r.attributeName === 'in-viewport' || r.attributeName === 'initial-slide') {
          requestAnimationFrame(update);
          break;
        }
      }
    };
    /** @type {MutationObserver | undefined} */
    let mo;
    if (slideshow instanceof HTMLElement) {
      mo = new MutationObserver(onSlideshowAttrs);
      mo.observe(slideshow, { attributes: true, attributeFilter: ['in-viewport', 'initial-slide'] });
    }

    requestAnimationFrame(update);
    const t = window.setTimeout(update, 150);

    return () => {
      window.clearTimeout(t);
      scroller.removeEventListener('scroll', update);
      ro.disconnect();
      mo?.disconnect();
    };
  }

  #syncAllCartAddonGroupsInRegion(region) {
    if (!(region instanceof HTMLElement) || !this.contains(region)) return;
    for (const group of region.querySelectorAll('[data-pdp-cp-cart-addon-group]')) {
      this.#syncCartAddonCardFromVariantPickers(/** @type {HTMLElement} */(group));
    }
  }

  /**
   * @param {Element | null | undefined} global
   * @returns {Map<number, string>}
   */
  #getCartAddonOptionMapFromGlobal(global) {
    const byPos = new Map();
    if (!(global instanceof HTMLElement)) return byPos;
    for (const sel of global.querySelectorAll('pdp-cp-custom-select[data-pdp-cp-cart-addon-option]')) {
      if (!(sel instanceof HTMLElement)) continue;
      const pos = Number.parseInt(sel.getAttribute('data-option-position') ?? '', 10);
      if (Number.isNaN(pos)) continue;
      const h = sel.querySelector('[data-pdp-cp-value-input]');
      if (h instanceof HTMLInputElement) {
        byPos.set(pos, (h.value || '').trim());
      }
    }
    return byPos;
  }

  /** @param {Event} e */
  #onCartAddonVariantChange = (e) => {
    if (!(e.target instanceof HTMLElement) || e.target.localName !== 'pdp-cp-custom-select') return;
    if (!e.target.hasAttribute('data-pdp-cp-cart-addon-option')) return;
    if (!this.contains(e.target)) return;
    if (!e.target.closest('[data-pdp-cp-cart-addons-global]')) return;
    const region = e.target.closest('.pdp-cp__cart-addons');
    if (region instanceof HTMLElement) this.#syncAllCartAddonGroupsInRegion(region);
  };

  /** @param {Event} e */
  #onGlobalCartAddonInput = (e) => {
    const t = e.target;
    if (!(t instanceof HTMLInputElement) || t.type !== 'hidden' || !t.hasAttribute('data-pdp-cp-value-input')) return;
    if (!t.closest('[data-pdp-cp-cart-addons-global]')) return;
    const region = t.closest('.pdp-cp__cart-addons');
    if (region instanceof HTMLElement) this.#syncAllCartAddonGroupsInRegion(region);
  };

  /**
   * Resolves the selected variant for a cart add-on (product) from the shared (global) option dropdowns
   * under the add-ons title and updates the card.
   * @param {HTMLElement} group
   */
  #syncCartAddonCardFromVariantPickers(group) {
    const region = group.closest('.pdp-cp__cart-addons');
    if (!(region instanceof HTMLElement)) return;

    const script = group.querySelector('script[type="application/json"][data-pdp-cp-cart-addon-variants]');
    const btn = group.querySelector('button[data-pdp-cp-cart-addon]');
    if (!script?.textContent || !(btn instanceof HTMLButtonElement)) return;

    /** @type {unknown} */
    let raw;
    try {
      raw = JSON.parse(script.textContent.trim());
    } catch {
      return;
    }
    const variants = /** @type {Array<{id: number; options: string[]; available?: boolean; priceFormatted?: string; label?: string; imageUrl?: string; imageUrl2x?: string}>} */ (Array.isArray(raw) ? raw : []);
    if (variants.length === 0) return;

    const global = region.querySelector('[data-pdp-cp-cart-addons-global]');
    if (!(global instanceof HTMLElement)) {
      const v0 = variants[0];
      if (v0) this.#applyCartAddonVariantToButton(group, btn, v0);
      return;
    }

    let byPos = this.#getCartAddonOptionMapFromGlobal(global);
    const lockOiStr = group.getAttribute('data-pdp-cp-cart-addon-lock-option-index');
    const lockVal = group.getAttribute('data-pdp-cp-cart-addon-lock-value');
    if (lockOiStr != null && lockOiStr !== '' && lockVal != null) {
      const li = Number.parseInt(lockOiStr, 10);
      if (Number.isFinite(li) && li >= 0) {
        byPos = new Map(byPos);
        byPos.set(li, (lockVal || '').trim());
      }
    }
    const optLen = Array.isArray(variants[0]?.options) ? variants[0].options.length : 0;
    if (optLen < 1) {
      const v0 = variants[0];
      if (v0) this.#applyCartAddonVariantToButton(group, btn, v0);
      return;
    }

    /** Always narrow by every option position that has a value in byPos (includes per-card lock / expand). */
    let cands = variants.slice();
    for (let i = 0; i < optLen; i += 1) {
      const val = (byPos.get(i) || '').trim();
      if (!val) continue;
      const next = cands.filter((w) => String(w?.options?.[i]) === val);
      if (next.length) cands = next;
      else break;
    }
    if (cands.length === 0) cands = variants.slice();

    const mat = cands.find((v) => v?.available) || cands[0];
    if (mat) this.#applyCartAddonVariantToButton(group, btn, mat);
  }

  /**
   * @param {HTMLElement} group
   * @param {HTMLButtonElement} btn
   * @param {{ id: number; options: string[]; available?: boolean; priceFormatted?: string; label?: string; imageUrl?: string; imageUrl2x?: string }} v
   */
  #applyCartAddonVariantToButton(group, btn, v) {
    const id = String(v.id);
    btn.setAttribute('data-variant-id', id);
    const av = v.available === true;
    btn.toggleAttribute('disabled', !av);
    btn.setAttribute('aria-disabled', !av ? 'true' : 'false');

    const priceText = (v.priceFormatted && String(v.priceFormatted).trim()) || '';

    const pTitle = (btn.getAttribute('data-product-title') || '').trim();
    const labelFmt = (btn.getAttribute('data-pdp-cp-ca-label-format') || 'simple').trim();
    const expandVal = (btn.getAttribute('data-pdp-cp-ca-expand-value') || '').trim();
    const labelEl = group.querySelector('[data-pdp-cp-cart-addon-label]');
    const nameLine =
      labelFmt === 'expand' && expandVal
        ? `${expandVal} x ${pTitle} (${priceText})`
        : `${pTitle} (${priceText})`;
    if (labelEl) labelEl.textContent = nameLine;
    const priceElHidden = group.querySelector('[data-pdp-cp-cart-addon-price]');
    if (priceElHidden instanceof HTMLElement) {
      priceElHidden.textContent = '';
      priceElHidden.setAttribute('hidden', '');
    }

    const tpl = this.#cartAddonI18n.addAria;
    const titleForAria = pTitle;
    const newAria = tpl.replace(/\[\[TITLE\]\]/g, titleForAria).replace(/\[\[PRICE\]\]/g, priceText);
    btn.setAttribute('aria-label', newAria);
    btn.setAttribute('data-initial-aria-label', newAria);

    const im = group.querySelector('img[data-pdp-cp-cart-addon-thumb-image]');
    const ph = group.querySelector('[data-pdp-cp-cart-addon-thumb-fallback]');
    const u = (v.imageUrl && String(v.imageUrl).trim()) || '';
    if (u && im instanceof HTMLImageElement) {
      im.removeAttribute('hidden');
      im.src = u;
      const u2 = (v.imageUrl2x && String(v.imageUrl2x).trim()) || '';
      if (u2) {
        im.setAttribute('srcset', `${u} 1x, ${u2} 2x`);
      } else {
        im.removeAttribute('srcset');
      }
      if (ph instanceof HTMLElement) ph.setAttribute('hidden', '');
    } else {
      if (im instanceof HTMLImageElement) {
        im.setAttribute('hidden', '');
        im.removeAttribute('srcset');
        im.src = 'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==';
      }
      if (ph instanceof HTMLElement) ph.removeAttribute('hidden');
    }
  }

  /** @param {MouseEvent} event */
  #onCartAddonClick = (event) => {
    const t = event.target;
    if (!(t instanceof Element)) return;
    const btn = t.closest('[data-pdp-cp-cart-addon]');
    if (!(btn instanceof HTMLButtonElement)) return;
    if (btn.disabled || btn.getAttribute('aria-disabled') === 'true') return;

    const group = btn.closest('[data-pdp-cp-cart-addon-group]');
    if (!(group instanceof HTMLElement)) return;

    const isSelected = this.#selectedAddonGroups.has(group);
    if (isSelected) {
      this.#selectedAddonGroups.delete(group);
    } else {
      this.#selectedAddonGroups.add(group);
    }
    this.#setCartAddonGroupSelectedState(group, !isSelected);
  };

  /**
   * Reflects selection on every addon button within a group.
   * @param {HTMLElement} group
   * @param {boolean} selected
   */
  #setCartAddonGroupSelectedState(group, selected) {
    for (const b of group.querySelectorAll('[data-pdp-cp-cart-addon]')) {
      if (!(b instanceof HTMLElement)) continue;
      b.classList.toggle('is-selected', selected);
      b.setAttribute('aria-pressed', selected ? 'true' : 'false');
    }
  }

  /**
   * Selected addons resolved to their current variant. Skips groups whose live
   * variant is unavailable/disabled or missing.
   * @returns {Array<{ id: number, quantity: number, title: string }>}
   */
  getSelectedAddons() {
    /** @type {Array<{ id: number, quantity: number, title: string }>} */
    const out = [];
    for (const group of this.#selectedAddonGroups) {
      if (!this.contains(group)) continue;
      const btn = group.querySelector('button[data-pdp-cp-cart-addon]');
      if (!(btn instanceof HTMLButtonElement)) continue;
      if (btn.disabled || btn.getAttribute('aria-disabled') === 'true') continue;
      const rawId = btn.getAttribute('data-variant-id')?.trim();
      const id = Number.parseInt(rawId ?? '', 10);
      if (Number.isNaN(id)) continue;
      const title = (btn.getAttribute('data-product-title') || '').trim();
      // Expand cards (one_card_per_option_name) encode the quantity to add, e.g. "3 x Title".
      const rawQty = btn.getAttribute('data-pdp-cp-ca-expand-value')?.trim();
      const parsedQty = Number.parseInt(rawQty ?? '', 10);
      const quantity = Number.isNaN(parsedQty) || parsedQty < 1 ? 1 : parsedQty;
      out.push({ id, quantity, title });
    }
    return out;
  }

  /** @param {Event} event */
  #onChange = (event) => {
    const t = event.target;
    if (!(t instanceof Element)) return;
    const presetHost = t.closest('pdp-cp-custom-select[data-preset-picker]');
    if (presetHost) {
      const hidden = presetHost.querySelector('[data-pdp-cp-value-input]');
      if (!(hidden instanceof HTMLInputElement)) return;
      const raw = hidden.value?.trim();
      if (raw === '') return;
      const idx = Number.parseInt(raw, 10);
      if (Number.isNaN(idx) || idx < 0) return;
      const preset = this.#presets[idx];
      if (!preset?.mappings?.length) return;
      this.#applyingPreset = true;
      try {
        const { keyOrder, by } = this.#groupPresetMappings(preset.mappings);
        for (const ik of keyOrder) {
          const g = by.get(ik);
          if (g) this.#applyFieldFromPresetMappings(ik, g);
        }
      } finally {
        queueMicrotask(() => {
          this.#applyingPreset = false;
        });
      }
      return;
    }
  };

  /** @param {Event} e */
  #onDescendantMaybeInvalidatePreset = (e) => {
    if (this.#applyingPreset) return;

    if (e.type === 'change' && e.target instanceof HTMLElement) {
      if (e.target.matches('pdp-cp-custom-select[data-preset-picker]')) return;
      const t = e.target;
      if (!(t instanceof HTMLTextAreaElement || t instanceof HTMLInputElement)) return;
      if (t.type === 'hidden' || t.type === 'radio' || t.type === 'checkbox') return;
      if (!t.hasAttribute('data-pdp-cp-property')) return;
    } else if (e.type === 'input') {
      const t = e.target;
      if (
        !(t instanceof HTMLInputElement) ||
        t.type !== 'hidden' ||
        !t.hasAttribute('data-pdp-cp-value-input')
      ) {
        return;
      }
      if (t.closest('pdp-cp-custom-select[data-preset-picker]')) return;
      if (t.closest('pdp-cp-custom-select[data-pdp-cp-cart-addon-option]')) return;
    } else {
      return;
    }

    this.#reconcilePresetSelection();
  };

  /** Drop color-scheme selection if any mapped value no longer matches. */
  #reconcilePresetSelection() {
    const presetHost = this.querySelector('pdp-cp-custom-select[data-preset-picker]');
    if (!presetHost) return;
    const hidden = presetHost.querySelector('[data-pdp-cp-value-input]');
    if (!(hidden instanceof HTMLInputElement)) return;
    const raw = hidden.value?.trim();
    if (raw === '') return;

    const idx = Number.parseInt(raw, 10);
    if (Number.isNaN(idx) || idx < 0 || idx >= this.#presets.length) {
      this.#clearPresetSelection();
      return;
    }

    const preset = this.#presets[idx];
    if (!preset?.mappings?.length) {
      this.#clearPresetSelection();
      return;
    }

    const { keyOrder, by } = this.#groupPresetMappings(preset.mappings);
    for (const ik of keyOrder) {
      const list = by.get(ik);
      if (!list?.length) continue;
      const expected = this.#expectedJoinForPresetMappings(ik, list);
      const actual = this.#readFieldValue(ik);
      if (actual !== expected) {
        this.#clearPresetSelection();
        return;
      }
      const el = this.querySelector(`pdp-cp-custom-select[data-internal-key="${ik}"]`);
      const qtyOn = el instanceof HTMLElement && el.dataset.selectQuantityEnabled === 'true';
      for (const m of list) {
        if (m.quantity == null) continue;
        const want = Number(m.quantity);
        if (!Number.isFinite(want) || want <= 0) continue;
        if (!qtyOn) continue;
        const val = String(m.value).trim();
        const wantQty = Math.floor(want);
        const host = /** @type {Element & { getQuantityForOptionValue?: (v: string) => number }} */ (el);
        if (typeof host.getQuantityForOptionValue === 'function') {
          if (host.getQuantityForOptionValue(val) !== wantQty) {
            this.#clearPresetSelection();
            return;
          }
        }
      }
    }
  }

  #clearPresetSelection() {
    const presetHost = this.querySelector('pdp-cp-custom-select[data-preset-picker]');
    if (!(presetHost instanceof HTMLElement) || typeof presetHost.setValueFromPreset !== 'function') return;
    const hidden = presetHost.querySelector('[data-pdp-cp-value-input]');
    if (!(hidden instanceof HTMLInputElement) || hidden.value === '') return;
    presetHost.setValueFromPreset('');
  }

  /**
   * @param {unknown} raw
   * @returns {{ keyOrder: string[]; by: Map<string, { internalKey?: string; value?: unknown; quantity?: unknown }[]> }}
   */
  #groupPresetMappings(raw) {
    /** @type {string[]} */
    const keyOrder = [];
    /** @type {Map<string, { internalKey?: string; value?: unknown; quantity?: unknown }[]>} */
    const by = new Map();
    if (!raw || !Array.isArray(raw)) return { keyOrder, by };
    for (const m of raw) {
      if (!m || typeof m !== 'object') continue;
      const obj = /** @type {{ internalKey?: string; value?: unknown; quantity?: unknown }} */ (m);
      if (obj.internalKey == null || obj.value == null) continue;
      const ik = String(obj.internalKey);
      if (!by.has(ik)) {
        by.set(ik, []);
        keyOrder.push(ik);
      }
      const arr = by.get(ik);
      if (arr) arr.push(obj);
    }
    return { keyOrder, by };
  }

  /**
   * @param {string} internalKey
   * @param {{ value?: unknown }[]} list
   * @returns {string}
   */
  #expectedJoinForPresetMappings(internalKey, list) {
    const el = this.querySelector(`pdp-cp-custom-select[data-internal-key="${internalKey}"]`);
    const host = /** @type {Element & { getJoinSeparator?: () => string }} */ (el);
    const sep = typeof host?.getJoinSeparator === 'function' ? host.getJoinSeparator() : DEFAULT_SEP;
    return list.map((m) => String(m.value).trim()).join(sep);
  }

  /**
   * @param {string} internalKey
   * @param {{ internalKey?: string; value?: unknown; quantity?: unknown }[]} list
   */
  #applyFieldFromPresetMappings(internalKey, list) {
    if (!list.length) return;
    if (list.length === 1) {
      this.#applyMapping(internalKey, String(list[0].value), list[0].quantity);
      return;
    }
    const el = this.querySelector(`pdp-cp-custom-select[data-internal-key="${internalKey}"]`);
    const entries = list.map((m) => ({
      value: String(m.value).trim(),
      quantity: m.quantity,
    }));
    if (el instanceof HTMLElement && 'setSelectionFromPreset' in el && typeof /** @type {any} */ (el).setSelectionFromPreset === 'function') {
      /** @type {(e: { value: string; quantity?: unknown }[]) => void} */ (/** @type {any} */ (el).setSelectionFromPreset.bind(el))(
        entries
      );
      return;
    }
    const key = CSS.escape(internalKey);
    const hidden = this.querySelector(`input[type="hidden"][data-pdp-cp-value-input][data-pdp-cp-property="${key}"]`);
    if (hidden instanceof HTMLInputElement) {
      const wrap = hidden.closest('pdp-cp-custom-select');
      if (wrap instanceof HTMLElement && 'setSelectionFromPreset' in wrap && typeof /** @type {any} */ (wrap).setSelectionFromPreset === 'function') {
        /** @type {(e: { value: string; quantity?: unknown }[]) => void} */ (/** @type {any} */ (wrap).setSelectionFromPreset.bind(
          wrap
        ))(entries);
        return;
      }
    }
    this.#applyMapping(internalKey, String(list[0].value), list[0].quantity);
  }

  /**
   * @param {string} internalKey
   * @returns {string}
   */
  #readFieldValue(internalKey) {
    for (const el of this.querySelectorAll('pdp-cp-custom-select[data-internal-key]')) {
      if (el.getAttribute('data-internal-key') !== internalKey) continue;
      const host = /** @type {Element & { getValueForPresetCompare?: () => string }} */ (el);
      if (typeof host.getValueForPresetCompare === 'function') {
        return host.getValueForPresetCompare();
      }
      const h = el.querySelector('[data-pdp-cp-value-input]');
      if (h instanceof HTMLInputElement) return (h.value || '').trim();
    }

    const key = CSS.escape(internalKey);
    const ta = this.querySelector(`textarea[data-pdp-cp-property="${key}"]`);
    if (ta instanceof HTMLTextAreaElement) return (ta.value || '').trim();

    const inp = this.querySelector(`input[data-pdp-cp-property="${key}"]`);
    if (inp instanceof HTMLInputElement && inp.type !== 'hidden') return (inp.value || '').trim();

    return '';
  }

  /**
   * @param {string} internalKey
   * @param {string} value
   * @param {unknown} [quantity] - from preset JSON; applied for custom selects in per-option quantity mode
   */
  #applyMapping(internalKey, value, quantity) {
    const el = this.querySelector(`pdp-cp-custom-select[data-internal-key="${internalKey}"]`);
    if (el instanceof HTMLElement && 'setValueFromPreset' in el && typeof el.setValueFromPreset === 'function') {
      const set = /** @type {(v: string, q?: number) => void} */ (el.setValueFromPreset.bind(el));
      if (quantity != null) {
        const n = Number(quantity);
        if (Number.isFinite(n) && n > 0) {
          set(value, n);
        } else {
          set(value);
        }
      } else {
        set(value);
      }
      return;
    }

    const key = CSS.escape(internalKey);

    const hidden = this.querySelector(`input[type="hidden"][data-pdp-cp-value-input][data-pdp-cp-property="${key}"]`);
    if (hidden instanceof HTMLInputElement) {
      const wrap = hidden.closest('pdp-cp-custom-select');
      if (wrap instanceof HTMLElement && typeof wrap.setValueFromPreset === 'function') {
        const set = /** @type {(v: string, q?: number) => void} */ (wrap.setValueFromPreset.bind(wrap));
        if (quantity != null) {
          const n = Number(quantity);
          if (Number.isFinite(n) && n > 0) {
            set(value, n);
          } else {
            set(value);
          }
        } else {
          set(value);
        }
        return;
      }
      hidden.value = value;
      return;
    }

    const select = this.querySelector(`select[data-pdp-cp-property="${key}"]`);
    if (select instanceof HTMLSelectElement) {
      select.value = value;
      return;
    }

    const textLike = this.querySelector(
      `input[data-pdp-cp-property="${key}"]:not([type="radio"]):not([type="checkbox"]):not([type="hidden"])`
    );
    if (textLike instanceof HTMLInputElement) {
      textLike.value = value;
      return;
    }

    const ta = this.querySelector(`textarea[data-pdp-cp-property="${key}"]`);
    if (ta instanceof HTMLTextAreaElement) {
      ta.value = value;
    }
  }
}

customElements.define('pdp-custom-properties-component', PdpCustomProperties);
